import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Plus, X, Camera, Image as ImageIcon, Archive, Trash2, Check, Sparkles, RefreshCw } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { WardrobeImg } from "@/components/WardrobeImg";
import { normaliseFormality, mapOccasionToTag } from "@/lib/aestheticEnums";
import { WardrobeDrawer } from "@/components/wardrobe/WardrobeDrawer";
import { GeneratingState } from "@/components/wardrobe/GeneratingState";
import { EmptyState } from "@/components/wardrobe/EmptyState";
import { EditItemSheet } from "@/components/wardrobe/EditItemSheet";
import { StyleYourself } from "@/components/wardrobe/StyleYourself";
import { SavedLooks } from "@/components/wardrobe/SavedLooks";
import { StyleYourselfResult } from "@/components/wardrobe/StyleYourselfResult";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { compressImage as normalizeForUpload } from "@/lib/imageCompression";

// Map WeatherKey to a placeholder °C (real weather inference is a backlog item).
const WEATHER_TO_TEMP_C: Record<string, number> = {
  hot: 28,
  sunny: 22,
  cool: 12,
  rainy: 14,
  cold: 4,
};

const CORAL = "#C84B2F";
const AMBER = "#EF9F27";
const AMBER_BG = "#FAEEDA";

type OccasionKey = "casual" | "work" | "smart_casual" | "date" | "party" | "wedding" | "festival";
type WeatherKey = "sunny" | "cool" | "rainy" | "cold" | "hot";
type MoodKey = "bold" | "minimal" | "cosy" | "edgy" | "feminine" | "sharp" | "polished";

const OCCASIONS: { key: OccasionKey; label: string }[] = [
  { key: "casual", label: "Casual" },
  { key: "work", label: "Work" },
  { key: "smart_casual", label: "Smart Casual" },
  { key: "date", label: "Date" },
  { key: "party", label: "Party" },
  { key: "wedding", label: "Wedding" },
  { key: "festival", label: "Festival" },
];
const WEATHERS: { key: WeatherKey; label: string }[] = [
  { key: "sunny", label: "☀️ Sunny" },
  { key: "cool", label: "⛅ Cool" },
  { key: "rainy", label: "🌧️ Rainy" },
  { key: "cold", label: "❄️ Cold" },
  { key: "hot", label: "🌡️ Hot" },
];
const MOODS: { key: MoodKey; label: string }[] = [
  { key: "bold", label: "✨ Bold" },
  { key: "minimal", label: "🤍 Minimal" },
  { key: "cosy", label: "🍂 Cosy" },
  { key: "edgy", label: "🖤 Edgy" },
  { key: "feminine", label: "🌸 Feminine" },
  { key: "sharp", label: "⚡ Sharp" },
  { key: "polished", label: "💼 Polished" },
];

function daysSinceWorn(item: { last_used_at: string | null }): number | null {
  if (!item.last_used_at) return null;
  const diff = Date.now() - new Date(item.last_used_at).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}
function isUnderused(item: { last_used_at: string | null }): boolean {
  const d = daysSinceWorn(item);
  if (d === null) return true;
  return d >= 14;
}
function lastWornLabel(item: { last_used_at: string | null }): string {
  const d = daysSinceWorn(item);
  if (d === null) return "Never worn";
  if (d === 0) return "Worn today";
  if (d === 1) return "Worn yesterday";
  return `Last worn ${d} days ago`;
}

// Mirrors backfill-wardrobe-items eligibility rule. Derives lb3_eligible
// from what detected_items reliably has (category + color_hex + material/
// style_tags). Silhouette/etc. are not on detected_items so are not factored.
function deriveLb3Eligible(p: {
  category: string | null | undefined;
  color_hex: string | null | undefined;
  material?: string | null;
  style_tags?: string[] | null;
}): boolean {
  if (!p.color_hex || !p.category || p.category === "other") return false;
  return !!(p.material || (p.style_tags && p.style_tags.length > 0));
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const isSame = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (isSame(d, today)) return "Today";
  const y = new Date();
  y.setDate(today.getDate() - 1);
  if (isSame(d, y)) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
function formatLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
}
function monthKey(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

export type WornOutfit = {
  id: string;
  user_id: string;
  wardrobe_item_ids: string[];
  generated_image_url: string | null;
  occasion: string | null;
  weather: string | null;
  mood: string | null;
  notes: string | null;
  worn_at: string | null;
  created_at?: string;
  is_saved_for_later?: boolean;
  sentiment?: "loved" | "good" | "okay" | null;
};


export type WardrobeItem = {
  id: string;
  user_id: string;
  name: string;
  category: string;
  color_hex: string | null;
  image_url: string | null;
  image_path: string | null;
  source: string;
  detected_item_id: string | null;
  last_used_at: string | null;
  times_used: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  vision_description?: string | null;
  lb3_description?: string | null;
  lb4_description?: string | null;
  size_purchased?: string | null;
  material?: string | null;
  style_tags?: string[] | null;
  lb3_eligible?: boolean | null;
  cutout_url?: string | null;
  cutout_status?: string | null;
};

/**
 * Fire-and-forget background-removal kickoff. Called after every
 * wardrobe_items insert site so the row picks up cutout_url/cutout_status
 * once the edge function completes; the UI receives the patch through the
 * realtime subscription mounted below.
 */
function invokeCutout(itemId: string) {
  void supabase.functions
    .invoke("generate-wardrobe-cutout", { body: { item_id: itemId } })
    .then(({ error }) => {
      if (error) {
        console.warn("[wardrobe-cutout] invoke failed for", itemId, error.message);
      }
    });
}



type SavedOutfitRow = {
  id: string;
  image_url: string;
  image_path: string | null;
  image_paths?: string[] | null;
  created_at: string;
  analysis_mode?: string | null;
  occasion?: string | null;
  style_summary?: string | null;
  title?: string | null;
  is_saved?: boolean | null;
};

const CATEGORIES = ["top", "bottom", "dress", "outerwear", "shoes", "bag", "accessory", "other"];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function compressImage(file: File, maxSize = 1024): Promise<string> {
  const dataUrl = await fileToBase64(file);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(dataUrl);
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

async function uploadWardrobeImage(userId: string, dataUrl: string): Promise<{ path: string; url: string } | null> {
  try {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const path = `${userId}/wardrobe/${crypto.randomUUID()}.jpg`;
    const { error } = await supabase.storage.from("outfits").upload(path, blob, {
      contentType: "image/jpeg",
      upsert: false,
    });
    if (error) throw error;
    const { data } = await supabase.storage.from("outfits").createSignedUrl(path, 60 * 60 * 24 * 7);
    return { path, url: data?.signedUrl ?? "" };
  } catch (e) {
    console.error("[wardrobe] upload failed", e);
    return null;
  }
}

type ProcessingItem = {
  id: string;
  fileName: string;
  status: "waiting" | "processing" | "done" | "error";
  name: string | null;
  category: string | null;
  color_hex: string | null;
};

function fileToBase64Parts(file: File): Promise<{ base64: string; mediaType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const parts = result.split(",");
      const mediaType = parts[0].split(":")[1].split(";")[0];
      resolve({ base64: parts[1], mediaType });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function uploadWardrobeFile(file: File, uid: string): Promise<string | null> {
  try {
    // WARDROBE-CUTOUT-1-NORMALIZE: canvas re-encode to clean sRGB JPEG before
    // upload, so the downstream cutout edge function gets bytes that
    // OpenAI's gpt-image-1 decoder accepts (rejects HEIC, Display P3, odd
    // EXIF). Shared primitive — same one used by shareHandler + routes/index.
    const normalized = await normalizeForUpload(file);
    const path = `${uid}/wardrobe/${Date.now()}-${Math.random().toString(36).slice(2)}.jpg`;
    const { error } = await supabase.storage.from("outfits").upload(path, normalized, {
      contentType: "image/jpeg",
      upsert: false,
    });
    if (error) {
      console.error("Upload error:", error);
      return null;
    }
    const { data } = await supabase.storage.from("outfits").createSignedUrl(path, 60 * 60 * 24 * 7);
    return data?.signedUrl ?? null;
  } catch (err) {
    console.error("uploadWardrobeFile:", err);
    return null;
  }
}

type UserProfileLite = {
  gender?: string | null;
  model_for?: string | null;
  model_setup_complete?: boolean | null;
  model_gender?: string | null;
  model_height?: string | null;
  model_body_shape?: string | null;
  model_body_size?: string | null;
  model_skin_tone?: string | null;
  model_hair?: string | null;
  model_preferred_variation?: string | null;
  model_preferred_variation_description?: string | null;
} | null;

export function Wardrobe({
  userId,
  wardrobeItems,
  setWardrobeItems,
  onViewOutfit,
  userProfile,
  onGoToShopping,
  lastKnownCity: lastKnownCityProp,
  todayWeather,
}: {
  userId: string;
  wardrobeItems: WardrobeItem[];
  setWardrobeItems: React.Dispatch<React.SetStateAction<WardrobeItem[]>>;
  onViewOutfit: (o: SavedOutfitRow) => void;
  userProfile?: UserProfileLite;
  onGoToShopping?: () => void;
  lastKnownCity?: string | null;
  todayWeather?: string | null;
}) {
  const navigate = useNavigate();
  const [lastKnownCity, setLastKnownCity] = useState<string | null>(lastKnownCityProp ?? null);
  useEffect(() => { setLastKnownCity(lastKnownCityProp ?? null); }, [lastKnownCityProp]);
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  type DetectedAttrs = {
    material?: string | null;
    pattern?: string | null;
    silhouette?: string | null;
    fit?: string | null;
    length?: string | null;
    neckline?: string | null;
    sleeve_length?: string | null;
    style_tags?: string[] | null;
    lb3_eligible?: boolean | null;
    lb3_description?: string | null;
  };

  const [singleConfirm, setSingleConfirm] = useState<({ name: string; category: string; color_hex: string; dataUrl: string } & DetectedAttrs) | null>(null);
  const [outfitConfirm, setOutfitConfirm] = useState<{ items: Array<{ name: string; category: string; color_hex: string; selected: boolean } & DetectedAttrs>; dataUrl: string } | null>(null);
  const [detecting, setDetecting] = useState(false);
  const [isProcessingItems, setIsProcessingItems] = useState(false);
  const [processingItems, setProcessingItems] = useState<ProcessingItem[]>([]);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [seeAll, setSeeAll] = useState(false);
  const [actionItem, setActionItem] = useState<WardrobeItem | null>(null);

  // === Phase 2: generation state ===
  const [selectedOccasion, setSelectedOccasion] = useState<OccasionKey>("casual");
  const [selectedWeather, setSelectedWeather] = useState<WeatherKey>("cool");
  useEffect(() => {
    if (todayWeather && ["sunny", "cool", "rainy", "cold", "hot"].includes(todayWeather)) {
      setSelectedWeather(todayWeather as WeatherKey);
    }
  }, [todayWeather]);
  const [selectedMood, setSelectedMood] = useState<MoodKey>("minimal");
  const [wardrobeGenerating, setWardrobeGenerating] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRoute, setDrawerRoute] = useState<"style-yourself" | "saved-looks" | "items" | "history" | "insights">("style-yourself");
  const [savedView, setSavedView] = useState<{ outfitId: string; lookbookImageUrl: string } | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("all");

  const [genError, setGenError] = useState<string | null>(null);
  const [wardrobeError, setWardrobeError] = useState<string | null>(null);
  const [genOutfitId, setGenOutfitId] = useState<string | null>(null);
  const [lookbookUrl, setLookbookUrl] = useState<string | null>(null);
  const [selectedLookItems, setSelectedLookItems] = useState<WardrobeItem[]>([]);
  const [styleNote, setStyleNote] = useState<string>("");
  const [swapTarget, setSwapTarget] = useState<WardrobeItem | null>(null);
  const [hasUnsavedSwap, setHasUnsavedSwap] = useState(false);
  const [swapCount, setSwapCount] = useState(0);
  const [wornSaved, setWornSaved] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const lookWornRef = useRef(false);
  const carouselOpenTimeRef = useRef<number | null>(null);
  const [carouselItemsViewed, setCarouselItemsViewed] = useState<Set<number>>(new Set());
  const [showSentiment, setShowSentiment] = useState(false);
  const [sentimentSubmitted, setSentimentSubmitted] = useState(false);
  const [showRegenerateFeedback, setShowRegenerateFeedback] = useState(false);
  const [generateTappedAt, setGenerateTappedAt] = useState<number | null>(null);
  const [currentWornOutfitId, setCurrentWornOutfitId] = useState<string | null>(null);

  const closeCarousel = () => {
    const timeInCarousel = carouselOpenTimeRef.current
      ? Math.round((Date.now() - carouselOpenTimeRef.current) / 1000)
      : 0;
    trackEvent("wardrobe_carousel_opened", {
      items_count: selectedLookItems.length,
      items_viewed_count: carouselItemsViewed.size,
      time_in_carousel_seconds: timeInCarousel,
      viewed_all: carouselItemsViewed.size === selectedLookItems.length,
    });
    carouselOpenTimeRef.current = null;
    setCarouselItemsViewed(new Set());
    setCarouselOpen(false);
  };

  const openCarouselAt = (index: number) => {
    setCarouselIndex(index);
    carouselOpenTimeRef.current = Date.now();
    setCarouselItemsViewed(new Set([index]));
    setCarouselOpen(true);
  };

  const getSupabaseImageUrl = (
    url: string | null | undefined,
    width: number,
    quality: number = 80,
  ): string | null => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      if (!parsed.hostname.includes("supabase.co")) return url;
      const isSignedUrl = parsed.pathname.includes("/object/sign/");
      if (isSignedUrl) {
        const renderPath = parsed.pathname.replace("/object/sign/", "/render/image/sign/");
        return `${parsed.origin}${renderPath}${parsed.search}&width=${width}&quality=${quality}&resize=contain`;
      }
      const isPublicUrl = parsed.pathname.includes("/object/public/");
      if (isPublicUrl) {
        const renderPath = parsed.pathname.replace("/object/public/", "/render/image/public/");
        return `${parsed.origin}${renderPath}?width=${width}&quality=${quality}&resize=contain`;
      }
      return url;
    } catch {
      return url;
    }
  };

  useEffect(() => {
    if (!carouselOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeCarousel();
      }
      if (e.key === "ArrowRight" && carouselIndex < selectedLookItems.length - 1) {
        setCarouselIndex((prev) => prev + 1);
      }
      if (e.key === "ArrowLeft" && carouselIndex > 0) {
        setCarouselIndex((prev) => prev - 1);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [carouselOpen, carouselIndex, selectedLookItems.length]);

  useEffect(() => {
    if (!carouselOpen) return;
    setCarouselItemsViewed((prev) => {
      if (prev.has(carouselIndex)) return prev;
      const next = new Set(prev);
      next.add(carouselIndex);
      return next;
    });
  }, [carouselIndex, carouselOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source");
    const utmMedium = params.get("utm_medium");
    const utmCampaign = params.get("utm_campaign");
    if (utmSource === "email" && utmMedium === "daily") {
      trackEvent("wardrobe_opened_from_email", {
        campaign: utmCampaign ?? "unknown",
        wardrobe_items_count: wardrobeItems.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (lookbookUrl && !lookWornRef.current) {
        trackEvent("wardrobe_look_abandoned", {
          occasion: selectedOccasion,
          weather: selectedWeather,
          mood: selectedMood,
          items_count: selectedLookItems.length,
          swapped_before_abandoning: swapCount > 0,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookbookUrl]);
  const [progressWidth, setProgressWidth] = useState(5);
  const [progressLabel, setProgressLabel] = useState("Picking your items");

  const moodOptions = (() => {
    const gender =
      userProfile?.model_gender ??
      userProfile?.gender ??
      "women"
    const isMale =
      gender === "men" ||
      gender === "male"
    if (isMale) {
      return [
        { value: "bold", label: "Bold" },
        { value: "minimal", label: "Minimal" },
        { value: "cosy", label: "Cosy" },
        { value: "edgy", label: "Edgy" },
        { value: "sharp", label: "Sharp" },
        { value: "polished", label: "Polished" },
      ]
    }
    return [
      { value: "bold", label: "Bold" },
      { value: "minimal", label: "Minimal" },
      { value: "cosy", label: "Cosy" },
      { value: "edgy", label: "Edgy" },
      { value: "feminine", label: "Feminine" },
      { value: "polished", label: "Polished" },
    ]
  })()

  useEffect(() => {
    const gender =
      userProfile?.model_gender ??
      userProfile?.gender ??
      "women"
    const isMale =
      gender === "men" ||
      gender === "male"
    if (
      isMale &&
      selectedMood === "feminine"
    ) {
      setSelectedMood("minimal")
    }
  }, [userProfile])

  useEffect(() => {
    const WARDROBE_STEPS = [
      { width: 8, label: "Picking your items" },
      { width: 20, label: "Checking colours" },
      { width: 35, label: "Matching your occasion" },
      { width: 50, label: "Styling the look" },
      { width: 65, label: "Generating your image" },
      { width: 80, label: "Almost there" },
    ];
    if (!wardrobeGenerating) {
      setProgressWidth(5);
      setProgressLabel("Picking your items");
      return;
    }
    let stepIndex = 0;
    const interval = setInterval(() => {
      if (stepIndex < WARDROBE_STEPS.length) {
        setProgressWidth(WARDROBE_STEPS[stepIndex].width);
        setProgressLabel(WARDROBE_STEPS[stepIndex].label);
        stepIndex++;
      } else {
        clearInterval(interval);
      }
    }, 12000);
    return () => clearInterval(interval);
  }, [wardrobeGenerating]);

  // === Phase 3: worn outfit history ===
  const [wornOutfits, setWornOutfits] = useState<WornOutfit[]>([]);
  const [historyView, setHistoryView] = useState<"main" | "all">("main");
  const [detailOutfit, setDetailOutfit] = useState<WornOutfit | null>(null);
  const [underusedSuggest, setUnderusedSuggest] = useState<WardrobeItem | null>(null);
  const [mustIncludeItemId, setMustIncludeItemId] = useState<string | null>(null);
  const [wardrobeTab, setWardrobeTab] = useState<"items" | "history" | "insights">("items");
  const generateRef = useRef<HTMLDivElement | null>(null);

  const singleFileRef = useRef<HTMLInputElement>(null);
  const outfitFileRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<number | null>(null);
  const pollStartTsRef = useRef<number>(0);
  const genOutfitIdRef = useRef<string | null>(null);

  const loadWornOutfits = async () => {
    if (!userId) return;
    const { data, error } = await supabase
      .from("worn_outfits")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) {
      console.error("loadWornOutfits error:", error);
      return;
    }
    if (data) {
      setWornOutfits(data as WornOutfit[]);
    }
  };

  useEffect(() => { loadWornOutfits(); /* eslint-disable-next-line */ }, [userId]);

  // Brief 3.4: TanStack Query's `enabled` flag gates the initial fetch.


  // Auto-hide wardrobeError after 5s
  useEffect(() => {
    if (!wardrobeError) return;
    const t = setTimeout(() => setWardrobeError(null), 5000);
    return () => clearTimeout(t);
  }, [wardrobeError]);

  // Realtime: merge cutout_url / cutout_status patches into local state as
  // generate-wardrobe-cutout completes. Scoped to this user's rows.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`wardrobe_cutout:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "wardrobe_items",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const next = payload.new as Partial<WardrobeItem> & { id?: string };
          if (!next?.id) return;
          setWardrobeItems((prev) =>
            prev.map((i) => (i.id === next.id ? { ...i, ...next } as WardrobeItem : i)),
          );
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId]);







  const visibleItems = seeAll ? wardrobeItems : wardrobeItems.slice(0, 12);
  const count = wardrobeItems.length;

  const callDetect = async (mode: "single" | "outfit", dataUrl: string) => {
    const { data, error } = await supabase.functions.invoke("detect-wardrobe-item", {
      body: { mode, imageBase64: dataUrl },
    });
    if (error) throw error;
    return data;
  };

  const handleSinglePhoto = async (file: File | undefined) => {
    if (!file) return;
    setAddSheetOpen(false);
    setDetecting(true);
    try {
      const dataUrl = await compressImage(file);
      const result = await callDetect("single", dataUrl);
      setSingleConfirm({
        name: result.name ?? "Untitled item",
        category: CATEGORIES.includes(result.category) ? result.category : "other",
        color_hex: result.color_hex ?? "#888888",
        dataUrl,
        material: result.material ?? null,
        pattern: result.pattern ?? null,
        silhouette: result.silhouette ?? null,
        fit: result.fit ?? null,
        length: result.length ?? null,
        neckline: result.neckline ?? null,
        sleeve_length: result.sleeve_length ?? null,
        style_tags: Array.isArray(result.style_tags) ? result.style_tags : [],
        lb3_eligible: !!result.lb3_eligible,
        lb3_description: result.lb3_description ?? null,
      });

    } catch (e) {
      console.error(e);
      toast.error("Couldn't detect item. Try another photo.");
    } finally {
      setDetecting(false);
    }
  };

  const handleOutfitPhoto = async (file: File | undefined) => {
    if (!file) return;
    setAddSheetOpen(false);
    setDetecting(true);
    try {
      const dataUrl = await compressImage(file);
      const result = await callDetect("outfit", dataUrl);
      const items = Array.isArray(result.items) ? result.items : [];
      if (items.length === 0) {
        toast.error("No items detected in this photo.");
        return;
      }
      setOutfitConfirm({
        items: items.map((i: any) => ({
          name: i.name ?? "Untitled item",
          category: CATEGORIES.includes(i.category) ? i.category : "other",
          color_hex: i.color_hex ?? "#888888",
          selected: true,
          material: i.material ?? null,
          pattern: i.pattern ?? null,
          silhouette: i.silhouette ?? null,
          fit: i.fit ?? null,
          length: i.length ?? null,
          neckline: i.neckline ?? null,
          sleeve_length: i.sleeve_length ?? null,
          style_tags: Array.isArray(i.style_tags) ? i.style_tags : [],
          lb3_eligible: !!i.lb3_eligible,
          lb3_description: i.lb3_description ?? null,
        })),

        dataUrl,
      });
    } catch (e) {
      console.error(e);
      toast.error("Couldn't detect items. Try another photo.");
    } finally {
      setDetecting(false);
    }
  };

  const handleItemPhotosSelected = async (input: React.ChangeEvent<HTMLInputElement> | File[]) => {
    const files = Array.isArray(input) ? input : Array.from(input.target.files ?? []);
    if (files.length === 0) return;



    setAddSheetOpen(false);
    setProcessingComplete(false);
    setIsProcessingItems(true);
    setProcessingItems(
      files.map((f, i) => ({
        id: `item-${i}-${Date.now()}`,
        fileName: f.name,
        status: "waiting" as const,
        name: null,
        category: null,
        color_hex: null,
      })),
    );

    const newItems: WardrobeItem[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      setProcessingItems((prev) =>
        prev.map((item, idx) => (idx === i ? { ...item, status: "processing" as const } : item)),
      );

      try {
        const { base64, mediaType } = await fileToBase64Parts(file);

        const { data: detection, error: fnError } = await supabase.functions.invoke(
          "detect-wardrobe-item",
          { body: { image_base64: base64, media_type: mediaType } },
        );

        if (fnError || !detection || (detection as any).error) {
          throw new Error((detection as any)?.error ?? fnError?.message ?? "Detection failed");
        }

        const imageUrl = await uploadWardrobeFile(file, userId);

        const detectedCategory = CATEGORIES.includes((detection as any).category)
          ? (detection as any).category
          : "other";

        const { data: inserted } = await supabase
          .from("wardrobe_items")
          .insert({
            user_id: userId,
            name: (detection as any).name,
            category: detectedCategory,
            color_hex: (detection as any).color_hex,
            image_url: imageUrl,
            image_path: null,
            source: "direct_upload",
            is_active: true,
            times_used: 0,
            material: (detection as any).material ?? null,
            pattern: (detection as any).pattern ?? null,
            silhouette: (detection as any).silhouette ?? null,
            fit: (detection as any).fit ?? null,
            length: (detection as any).length ?? null,
            neckline: (detection as any).neckline ?? null,
            sleeve_length: (detection as any).sleeve_length ?? null,
            style_tags: Array.isArray((detection as any).style_tags) ? (detection as any).style_tags : [],
            lb3_eligible: !!(detection as any).lb3_eligible,
            lb3_description: (detection as any).lb3_description ?? null,
            vision_description: (detection as any).vision_description ?? null,
            formality: (detection as any).formality ?? null,
            occasion_tags: Array.isArray((detection as any).occasion_tags) ? (detection as any).occasion_tags : null,
          })

          .select()
          .single();

        if (inserted) {
          newItems.push(inserted as WardrobeItem);
          invokeCutout((inserted as WardrobeItem).id);
        }

        setProcessingItems((prev) =>
          prev.map((item, idx) =>
            idx === i
              ? {
                  ...item,
                  status: "done" as const,
                  name: (detection as any).name,
                  category: detectedCategory,
                  color_hex: (detection as any).color_hex,
                }
              : item,
          ),
        );
      } catch (err) {
        console.error(`Item ${i} failed:`, err);
        setProcessingItems((prev) =>
          prev.map((item, idx) => (idx === i ? { ...item, status: "error" as const } : item)),
        );
      }
    }

    if (newItems.length > 0) {
      setWardrobeItems((prev) => [...newItems, ...prev]);
    }

    trackEvent("wardrobe_items_added", {
      source: "direct_upload",
      count_added: newItems.length,
      count_failed: files.length - newItems.length,
      total_wardrobe_size_after: wardrobeItems.length + newItems.length,
    });

    setIsProcessingItems(false);
    setProcessingComplete(true);
    if (!Array.isArray(input)) input.target.value = "";
  };






  const saveSingleItem = async () => {
    if (!singleConfirm) return;
    const upload = await uploadWardrobeImage(userId, singleConfirm.dataUrl);
    const { data, error } = await supabase
      .from("wardrobe_items")
      .insert({
        user_id: userId,
        name: singleConfirm.name,
        category: singleConfirm.category,
        color_hex: singleConfirm.color_hex,
        image_url: upload?.url ?? null,
        image_path: upload?.path ?? null,
        source: "direct_upload",
        material: singleConfirm.material ?? null,
        pattern: singleConfirm.pattern ?? null,
        silhouette: singleConfirm.silhouette ?? null,
        fit: singleConfirm.fit ?? null,
        length: singleConfirm.length ?? null,
        neckline: singleConfirm.neckline ?? null,
        sleeve_length: singleConfirm.sleeve_length ?? null,
        style_tags: Array.isArray(singleConfirm.style_tags) ? singleConfirm.style_tags : [],
        lb3_eligible: !!singleConfirm.lb3_eligible,
        lb3_description: singleConfirm.lb3_description ?? null,
        vision_description: (singleConfirm as any).vision_description ?? null,
        formality: (singleConfirm as any).formality ?? null,
        occasion_tags: Array.isArray((singleConfirm as any).occasion_tags) ? (singleConfirm as any).occasion_tags : null,
      })

      .select()
      .single();
    if (error) {
      toast.error("Couldn't save item.");
      return;
    }
    setWardrobeItems((prev) => [data as WardrobeItem, ...prev]);
    invokeCutout((data as WardrobeItem).id);
    setSingleConfirm(null);
    toast.success("Added to wardrobe");
  };

  const saveOutfitItems = async () => {
    if (!outfitConfirm) return;
    const selected = outfitConfirm.items.filter((i) => i.selected);
    if (selected.length === 0) {
      setOutfitConfirm(null);
      return;
    }
    const upload = await uploadWardrobeImage(userId, outfitConfirm.dataUrl);
    // If only one item was selected, treat as a single-item upload. Mode 2
    // (outfit_photo) shares one image across rows; a 1-item "outfit" is
    // functionally Mode 1 and avoids the shared-image edge case.
    const effectiveSource = selected.length === 1 ? "direct_upload" : "outfit_photo";
    const rows = selected.map((i) => ({
      user_id: userId,
      name: i.name,
      category: i.category,
      color_hex: i.color_hex,
      image_url: upload?.url ?? null,
      image_path: upload?.path ?? null,
      source: effectiveSource,
      material: i.material ?? null,
      pattern: i.pattern ?? null,
      silhouette: i.silhouette ?? null,
      fit: i.fit ?? null,
      length: i.length ?? null,
      neckline: i.neckline ?? null,
      sleeve_length: i.sleeve_length ?? null,
      style_tags: Array.isArray(i.style_tags) ? i.style_tags : [],
      lb3_eligible: !!i.lb3_eligible,
      lb3_description: i.lb3_description ?? null,
      vision_description: (i as any).vision_description ?? null,
      formality: (i as any).formality ?? null,
      occasion_tags: Array.isArray((i as any).occasion_tags) ? (i as any).occasion_tags : null,
    }));

    const { data, error } = await supabase.from("wardrobe_items").insert(rows).select();
    if (error) {
      toast.error("Couldn't save items.");
      return;
    }
    setWardrobeItems((prev) => [...(data as WardrobeItem[]), ...prev]);
    (data as WardrobeItem[]).forEach((row) => invokeCutout(row.id));
    trackEvent("wardrobe_items_added", {
      source: effectiveSource,
      count_added: (data as WardrobeItem[]).length,
      count_failed: 0,
      total_wardrobe_size_after: wardrobeItems.length + (data as WardrobeItem[]).length,
    });
    setOutfitConfirm(null);
    toast.success(`Added ${selected.length} item${selected.length === 1 ? "" : "s"}`);
  };

  const removeItem = async (item: WardrobeItem) => {
    setActionItem(null);
    const { error } = await supabase
      .from("wardrobe_items")
      .update({ is_active: false })
      .eq("id", item.id);
    if (error) {
      toast.error("Couldn't remove item.");
      return;
    }
    setWardrobeItems((prev) => prev.filter((i) => i.id !== item.id));
    toast.success("Item removed");
  };

  async function handleSaveItem(updates: { name: string; category: string; size_purchased: string | null }) {
    if (!actionItem) return;
    const original = actionItem;
    const categoryChanged = updates.category !== original.category;

    const next: WardrobeItem = { ...original, ...updates };
    if (categoryChanged) {
      next.lb3_eligible = deriveLb3Eligible(next);
    }

    setWardrobeItems((prev) => prev.map((i) => (i.id === original.id ? next : i)));
    setActionItem(null);

    const writeData: {
      name: string;
      category: string;
      size_purchased: string | null;
      lb3_eligible?: boolean;
    } = {
      name: updates.name,
      category: updates.category,
      size_purchased: updates.size_purchased,
    };
    if (categoryChanged) {
      writeData.lb3_eligible = next.lb3_eligible ?? false;
    }

    const { error } = await supabase
      .from("wardrobe_items")
      .update(writeData)
      .eq("id", original.id);

    if (error) {
      setWardrobeItems((prev) => prev.map((i) => (i.id === original.id ? original : i)));
      toast.error("Couldn't update item");
      return;
    }

    toast.success("Item updated");
    trackEvent("wardrobe_item_edited", {
      item_id: original.id,
      name_changed: updates.name !== original.name,
      category_changed: categoryChanged,
      size_changed: updates.size_purchased !== (original.size_purchased ?? null),
    });
  }

  async function handleConfirmDelete() {
    if (!actionItem) return;
    const itemToDelete = actionItem;
    await removeItem(itemToDelete);
    trackEvent("wardrobe_item_delete_confirmed", { item_id: itemToDelete.id });
  }


  // === Phase 2 helpers ===
  const stopPolling = () => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => () => stopPolling(), []);

  const buildModelSetup = () => {
    if (!userProfile?.model_setup_complete) return null;
    const variation = userProfile.model_preferred_variation ?? null;
    const variationDescription = variation
      ? userProfile.model_preferred_variation_description ?? null
      : null;
    return {
      height: userProfile.model_height ?? undefined,
      body_shape: userProfile.model_body_shape ?? undefined,
      body_size: userProfile.model_body_size ?? undefined,
      skin_tone: userProfile.model_skin_tone ?? undefined,
      hair: userProfile.model_hair ?? undefined,
      variation: variation ?? undefined,
      variation_description: variationDescription ?? undefined,
    };
  };

  const invokeWardrobeGenerate = async (
    outfitId: string,
    itemsForLook: WardrobeItem[],
    mustIncludeItemIdsOverride?: string[],
  ) => {
    const effectiveGender = (() => {
      if (
        userProfile?.model_for === "myself" &&
        userProfile?.model_gender
      ) {
        return userProfile.model_gender;
      }
      return userProfile?.gender ?? "women";
    })();
    const payload: Record<string, unknown> = {
      outfit_id: outfitId,
      user_id: userId,
      wardrobe_items: itemsForLook.map((i) => ({
        id: i.id,
        name: i.name,
        category: i.category,
        color_hex: i.color_hex,
        image_url: i.image_url,
        image_path: i.image_path ?? null,
        last_used_at: i.last_used_at,
        times_used: i.times_used,
        vision_description: i.vision_description ?? null,
        lb3_description: i.lb3_description ?? null,
        lb4_description: i.lb4_description ?? null,
      })),

      occasion: selectedOccasion,
      weather: selectedWeather,
      mood: selectedMood,
      gender: effectiveGender,
      model_setup: buildModelSetup(),
      must_include_item_id: mustIncludeItemId ?? undefined,
    };
    if (mustIncludeItemIdsOverride && mustIncludeItemIdsOverride.length > 0) {
      payload.must_include_item_ids = mustIncludeItemIdsOverride;
    }
    const { error } = await supabase.functions.invoke("generate-lookbook4", { body: payload });
    if (error) {
      console.error("[lb4-client] invoke_failed", JSON.stringify({
        outfit_id: outfitId,
        error: String(error),
        ts: Date.now(),
      }));
      throw error;
    }
  };

  const startPolling = (outfitId: string) => {
    stopPolling();
    const pollStartTs = Date.now();
    pollStartTsRef.current = pollStartTs;
    genOutfitIdRef.current = outfitId;
    console.log("[lb4-client] poll_start", JSON.stringify({
      outfit_id: outfitId,
      ts: pollStartTs,
    }));
    pollRef.current = window.setInterval(async () => {
      console.log("[lb4-client] poll_tick", JSON.stringify({
        outfit_id: outfitId,
        elapsed_ms: Date.now() - pollStartTs,
        ts: Date.now(),
      }));
      const { data } = await supabase
        .from("outfits")
        .select("lookbook_status, lookbook_image_url, lookbook_error, selected_wardrobe_items, wardrobe_style_note")
        .eq("id", outfitId)
        .maybeSingle();
      const row = data as {
        lookbook_status?: string | null;
        lookbook_image_url?: string | null;
        lookbook_error?: string | null;
        selected_wardrobe_items?: WardrobeItem[] | null;
        wardrobe_style_note?: string | null;
      } | null;
      if (!row) return;
      if (row.lookbook_status === "ready" && row.lookbook_image_url) {
        console.log("[lb4-client] poll_ready", JSON.stringify({
          outfit_id: outfitId,
          total_duration_ms: Date.now() - pollStartTs,
          ts: Date.now(),
        }));
        stopPolling();
        setLookbookUrl(row.lookbook_image_url);
        const selIds = (row.selected_wardrobe_items ?? []).map((s) => s.id);
        const liveItems = wardrobeItems.filter((wi) => selIds.includes(wi.id));
        setSelectedLookItems(liveItems.length ? liveItems : (row.selected_wardrobe_items ?? []) as WardrobeItem[]);
        if (row.wardrobe_style_note) setStyleNote(row.wardrobe_style_note);
        setWardrobeGenerating(false);
        genOutfitIdRef.current = null;
      } else if (row.lookbook_status === "failed") {
        console.error("[lb4-client] poll_failed", JSON.stringify({
          outfit_id: outfitId,
          total_duration_ms: Date.now() - pollStartTs,
          error: row.lookbook_error ?? "unknown",
          ts: Date.now(),
        }));
        stopPolling();
        setGenError(row.lookbook_error ?? "generation_failed");
        setWardrobeError("Something went wrong while generating your look. Please try again.");
        setWardrobeGenerating(false);
        toast.error("Couldn't generate look. Try again.");
        genOutfitIdRef.current = null;
      }
    }, 3000);
  };

  useEffect(() => {
    if (!wardrobeGenerating) return;
    const timeout = setTimeout(() => {
      if (wardrobeGenerating) {
        console.error("[lb4-client] poll_client_timeout", JSON.stringify({
          outfit_id: genOutfitIdRef.current ?? "unknown",
          timeout_ms: 180_000,
          ts: Date.now(),
        }));
        stopPolling();
        setWardrobeGenerating(false);
        setWardrobeError("Generation took too long. Please try again.");
        genOutfitIdRef.current = null;
      }
    }, 3 * 60 * 1000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wardrobeGenerating]);

  const buildItemShortlist = (
    items: WardrobeItem[],
    occasion: string,
    weather: string,
    mood: string,
  ): WardrobeItem[] => {
    const sortByLastUsed = (arr: WardrobeItem[]) =>
      [...arr].sort((a, b) => {
        if (!a.last_used_at) return -1;
        if (!b.last_used_at) return 1;
        return (
          new Date(a.last_used_at).getTime() -
          new Date(b.last_used_at).getTime()
        );
      });

    const nameContains = (item: WardrobeItem, terms: string[]) =>
      terms.some((t) => item.name.toLowerCase().includes(t.toLowerCase()));

    let tops = items.filter((i) => i.category === "top");
    let bottoms = items.filter((i) => i.category === "bottom");
    let dresses = items.filter((i) => i.category === "dress");
    let shoes = items.filter((i) => i.category === "shoes");
    let outerwear = items.filter((i) => i.category === "outerwear");
    let bags = items.filter((i) => i.category === "bag");

    if (weather === "cold" || weather === "rainy") {
      shoes = shoes.filter(
        (i) => !nameContains(i, ["sandal", "flip flop", "slide", "open toe"]),
      );
      dresses = dresses.filter(
        (i) => !nameContains(i, ["summer", "mini", "strapless", "sundress"]),
      );
    }

    if (weather === "hot" || weather === "sunny") {
      outerwear = outerwear.filter(
        (i) =>
          !nameContains(i, [
            "puffer",
            "parka",
            "padded",
            "quilted",
            "heavy",
            "wool coat",
          ]),
      );
    }

    if (mood === "minimal") {
      const neutralTerms = [
        "white",
        "black",
        "grey",
        "gray",
        "navy",
        "beige",
        "cream",
        "tan",
        "brown",
        "khaki",
        "charcoal",
      ];
      const isNeutral = (i: WardrobeItem) => nameContains(i, neutralTerms);
      tops = [...tops.filter(isNeutral), ...tops.filter((i) => !isNeutral(i))];
      bottoms = [
        ...bottoms.filter(isNeutral),
        ...bottoms.filter((i) => !isNeutral(i)),
      ];
    }

    if (mood === "bold") {
      const boldTerms = [
        "red",
        "orange",
        "yellow",
        "green",
        "purple",
        "pink",
        "printed",
        "pattern",
        "striped",
        "floral",
        "graphic",
        "coloured",
        "colored",
        "bright",
      ];
      const isBold = (i: WardrobeItem) => nameContains(i, boldTerms);
      tops = [...tops.filter(isBold), ...tops.filter((i) => !isBold(i))];
    }

    tops = sortByLastUsed(tops);
    bottoms = sortByLastUsed(bottoms);
    dresses = sortByLastUsed(dresses);
    shoes = sortByLastUsed(shoes);
    outerwear = sortByLastUsed(outerwear);
    bags = sortByLastUsed(bags);

    const needsOuterwear = weather === "cold" || weather === "rainy";
    const shortlist: WardrobeItem[] = [];

    if (dresses.length > 0 && occasion !== "work") {
      shortlist.push(...dresses.slice(0, 1));
      shortlist.push(...tops.slice(0, 1));
    } else {
      shortlist.push(...tops.slice(0, 2));
      shortlist.push(...bottoms.slice(0, 2));
    }
    shortlist.push(...shoes.slice(0, 2));
    if (needsOuterwear && outerwear.length > 0) {
      shortlist.push(...outerwear.slice(0, 2));
    } else if (outerwear.length > 0) {
      shortlist.push(...outerwear.slice(0, 1));
    }
    if (bags.length > 0) {
      shortlist.push(...bags.slice(0, 1));
    }

    return shortlist.slice(0, 12);
  };

  const handleGenerate = async () => {
    if (wardrobeItems.length < 5 || wardrobeGenerating) return;

    const QUICK_REGEN_THRESHOLD = 30000;
    if (
      lookbookUrl &&
      generateTappedAt &&
      Date.now() - generateTappedAt < QUICK_REGEN_THRESHOLD
    ) {
      setShowRegenerateFeedback(true);
      return;
    }
    setGenerateTappedAt(Date.now());

    setGenError(null);
    setWardrobeError(null);
    setLookbookUrl(null);
    setSelectedLookItems([]);
    setStyleNote("");
    setWornSaved(false);
    setHasUnsavedSwap(false);
    setSwapCount(0);
    setShowSentiment(false);
    setSentimentSubmitted(false);
    setCurrentWornOutfitId(null);

    const shortlist = buildItemShortlist(
      wardrobeItems,
      selectedOccasion,
      selectedWeather,
      selectedMood,
    );
    if (shortlist.length < 3) {
      toast.error("Add more items to your wardrobe to generate a look");
      return;
    }

    setWardrobeGenerating(true);
    try {
      const { data: newOutfit, error: insertErr } = await supabase
        .from("outfits")
        .insert({
          user_id: userId,
          image_url: "",
          analysis_mode: "wardrobe",
          occasion: selectedOccasion,
          is_saved: false,
        })
        .select()
        .single();
      if (insertErr || !newOutfit) throw insertErr ?? new Error("insert failed");
      setGenOutfitId(newOutfit.id);
      await invokeWardrobeGenerate(newOutfit.id, shortlist);
      trackEvent("wardrobe_look_generated", {
        occasion: selectedOccasion,
        weather: selectedWeather,
        mood: selectedMood,
        items_count: shortlist.length,
      });
      startPolling(newOutfit.id);
    } catch (e) {
      console.error("[wardrobe] generate error", e);
      setWardrobeGenerating(false);
      toast.error("Couldn't start generation.");
    }
  };







  const handleRegenerate = async () => {
    if (!genOutfitId || wardrobeGenerating) return;
    setGenError(null);
    setLookbookUrl(null);
    setWornSaved(false);
    setWardrobeGenerating(true);
    try {
      // Use the currently selected (post-swap) items as the candidate pool first,
      // plus the rest of the wardrobe for completeness.
      const ids = new Set(selectedLookItems.map((i) => i.id));
      const ordered = [
        ...selectedLookItems,
        ...wardrobeItems.filter((i) => !ids.has(i.id)),
      ];
      await invokeWardrobeGenerate(genOutfitId, ordered);
      trackEvent("wardrobe_look_regenerated", {
        occasion: selectedOccasion,
        swap_count: swapCount,
      });
      setHasUnsavedSwap(false);
      startPolling(genOutfitId);
    } catch (e) {
      console.error(e);
      setWardrobeGenerating(false);
      toast.error("Couldn't regenerate.");
    }
  };

  const performSwap = (replacement: WardrobeItem) => {
    if (!swapTarget) return;
    setSelectedLookItems((prev) =>
      prev.map((i) => (i.id === swapTarget.id ? replacement : i)),
    );
    setHasUnsavedSwap(true);
    setSwapCount((c) => c + 1);
    trackEvent("wardrobe_item_swap_detail", {
      from_item_id: swapTarget.id,
      from_item_category: swapTarget.category,
      to_item_id: replacement.id,
      swap_number_in_session: swapCount + 1,
      occasion: selectedOccasion,
      mood: selectedMood,
    });
    // LB4 signal capture: swap is a negative-then-positive signal.
    if (userId && genOutfitId) {
      void supabase
        .from("outfit_feedback" as never)
        .insert({
          user_id: userId,
          outfit_id: genOutfitId,
          feedback_type: "swap_item",
          reason: `swap:${swapTarget.id}->${replacement.id}`,
          occasion: selectedOccasion,
          weather: selectedWeather,
          mood: selectedMood,
          item_ids: [swapTarget.id, replacement.id],
        } as never)
        .then(({ error }) => {
          if (error) console.error("[lb4] swap signal insert failed", error);
        });
    }
    setSwapTarget(null);
  };

  const handleWoreThis = async () => {
    if (!lookbookUrl || wornSaved) return;
    const ids = selectedLookItems.map((i) => i.id);
    try {
      const { data: insertedWorn } = await supabase
        .from("worn_outfits")
        .insert({
          user_id: userId,
          generated_image_url: lookbookUrl,
          occasion: selectedOccasion,
          weather: selectedWeather,
          mood: selectedMood,
          wardrobe_item_ids: ids,
          worn_at: new Date().toISOString(),
        })
        .select()
        .single();
      await Promise.all(ids.map((id) => supabase.rpc("increment" as never, { row_id: id } as never)));
      // refresh local wardrobeItems counts/last_used_at
      const { data: refreshed } = await supabase
        .from("wardrobe_items")
        .select("*")
        .eq("user_id", userId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      if (refreshed) setWardrobeItems(refreshed as WardrobeItem[]);
      setWornSaved(true);
      lookWornRef.current = true;
      loadWornOutfits();
      trackEvent("wardrobe_look_worn", {
        occasion: selectedOccasion,
        weather: selectedWeather,
        mood: selectedMood,
      });
      // LB4 signal capture: positive signal for the worn outfit.
      if (genOutfitId) {
        void supabase
          .from("outfit_feedback" as never)
          .insert({
            user_id: userId,
            outfit_id: genOutfitId,
            feedback_type: "mark_worn",
            occasion: selectedOccasion,
            weather: selectedWeather,
            mood: selectedMood,
            item_ids: ids,
          } as never)
          .then(({ error }) => {
            if (error) console.error("[lb4] mark_worn signal insert failed", error);
          });
      }
      toast.success("Outfit saved to your history 💫");
      if (insertedWorn?.id) {
        setCurrentWornOutfitId(insertedWorn.id);
        setTimeout(() => setShowSentiment(true), 800);
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save outfit.");
    }
  };

  const handleSentiment = async (sentiment: "loved" | "good" | "okay") => {
    setSentimentSubmitted(true);
    trackEvent("wardrobe_look_sentiment", {
      sentiment,
      occasion: selectedOccasion,
      weather: selectedWeather,
      mood: selectedMood,
      items_count: selectedLookItems.length,
      item_ids: selectedLookItems.map((i) => i.id),
    });
    if (currentWornOutfitId) {
      try {
        await supabase
          .from("worn_outfits")
          .update({ sentiment } as never)
          .eq("id", currentWornOutfitId);
      } catch (err) {
        console.error("Sentiment update failed:", err);
      }
    }
    setTimeout(() => {
      setShowSentiment(false);
    }, 2000);
  };

  const handleRegenerateFeedback = async (reason: string) => {
    setShowRegenerateFeedback(false);
    trackEvent("wardrobe_regeneration_feedback", {
      reason,
      occasion: selectedOccasion,
      weather: selectedWeather,
      mood: selectedMood,
      item_ids: selectedLookItems.map((i) => i.id),
    });
    try {
      if (userId && genOutfitId) {
        await supabase
          .from("outfit_feedback" as never)
          .insert({
            user_id: userId,
            outfit_id: genOutfitId,
            feedback_type: "immediate_regeneration",
            reason,
            occasion: selectedOccasion,
            weather: selectedWeather,
            mood: selectedMood,
            item_ids: selectedLookItems.map((i) => i.id),
          } as never);
      }
    } catch (err) {
      console.error("Feedback insert failed:", err);
    }
    // Reset throttle so the next handleGenerate call proceeds normally
    setGenerateTappedAt(null);
    handleGenerate();
  };

  const handleShopLook = () => {
    trackEvent("wardrobe_to_shopping_detail", {
      occasion: selectedOccasion,
      weather: selectedWeather,
      mood: selectedMood,
      items_in_look: selectedLookItems.length,
      look_was_worn: lookWornRef.current,
      wardrobe_size: wardrobeItems.length,
    });
    onGoToShopping?.();
  };

  const swapOptions = useMemo(() => {
    if (!swapTarget) return [];
    const usedIds = new Set(selectedLookItems.map((i) => i.id));
    return wardrobeItems.filter(
      (i) => i.category === swapTarget.category && i.id !== swapTarget.id && !usedIds.has(i.id),
    );
  }, [swapTarget, selectedLookItems, wardrobeItems]);

  const underusedItems = useMemo(
    () => wardrobeItems.filter(isUnderused),
    [wardrobeItems],
  );

  const shouldShowUnderusedDots = useMemo(() => {
    if (wardrobeItems.length === 0) return false;
    const underusedCount = wardrobeItems.filter(isUnderused).length;
    return wornOutfits.length >= 5 && underusedCount <= wardrobeItems.length * 0.5;
  }, [wardrobeItems, wornOutfits]);

  const wardrobeInsights = useMemo(() => {
    if (wardrobeItems.length === 0) return null;
    const underusedCount = wardrobeItems.filter(isUnderused).length;
    const mostUsedItem = wardrobeItems.reduce(
      (max, item) => ((item.times_used ?? 0) > (max.times_used ?? 0) ? item : max),
      wardrobeItems[0],
    );
    const totalOutfits = wornOutfits.length;
    const topOccasion = wornOutfits.length > 0
      ? Object.entries(
          wornOutfits.reduce((acc, o) => {
            const occ = o.occasion ?? "casual";
            acc[occ] = (acc[occ] ?? 0) + 1;
            return acc;
          }, {} as Record<string, number>),
        ).sort((a, b) => b[1] - a[1])[0][0]
      : null;
    return { underusedCount, mostUsedItem, totalOutfits, topOccasion };
  }, [wardrobeItems, wornOutfits]);

  const insightIndex = new Date().getDate() % 3;

  const wornById = useMemo(() => {
    const map = new Map<string, WardrobeItem>();
    wardrobeItems.forEach((i) => map.set(i.id, i));
    return map;
  }, [wardrobeItems]);

  const triggerGenerateFromUnderused = (item: WardrobeItem) => {
    setUnderusedSuggest(null);
    setSelectedOccasion("casual");
    setMustIncludeItemId(item.id);
    trackEvent("wardrobe_underused_generate", { item_id: item.id });
    setTimeout(() => {
      generateRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      handleGenerate();
    }, 50);
  };

  const wearAgain = (o: WornOutfit) => {
    setDetailOutfit(null);
    if (o.occasion) setSelectedOccasion(o.occasion as OccasionKey);
    if (o.weather) setSelectedWeather(o.weather as WeatherKey);
    if (o.mood) setSelectedMood(o.mood as MoodKey);
    const liveItems = o.wardrobe_item_ids.map((id) => wornById.get(id)).filter((x): x is WardrobeItem => !!x);
    setSelectedLookItems(liveItems);
    trackEvent("wardrobe_wear_again", { outfit_id: o.id });
    setTimeout(() => {
      generateRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      handleGenerate();
    }, 50);
  };

  // Clear must-include flag after generation completes
  useEffect(() => {
    if (lookbookUrl) {
      setMustIncludeItemId(null);
      lookWornRef.current = false;
      setSwapCount(0);
      setGenerateTappedAt(Date.now());
    }
  }, [lookbookUrl]);

  const formatRelativeDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "Saved";
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const handleSaveForLater = async () => {
    if (!userId || !lookbookUrl) return;
    try {
      const { data: inserted } = await supabase
        .from("worn_outfits")
        .insert({
          user_id: userId,
          generated_image_url: lookbookUrl,
          occasion: selectedOccasion,
          weather: selectedWeather,
          mood: selectedMood,
          wardrobe_item_ids: selectedLookItems.map((i) => i.id),
          is_saved_for_later: true,
          worn_at: null,
          notes: null,
        } as never)
        .select()
        .single();
      if (inserted) {
        setWornOutfits((prev) => [inserted as WornOutfit, ...prev]);
        trackEvent("wardrobe_look_saved_for_later", {
          occasion: selectedOccasion,
          weather: selectedWeather,
          mood: selectedMood,
          items_count: selectedLookItems.length,
        });
        toast.success("Saved to your history");
      }
    } catch (err) {
      console.error("Save for later failed:", err);
    }
  };

  const handleWearSavedLook = async (outfit: WornOutfit) => {
    try {
      const now = new Date().toISOString();
      await supabase
        .from("worn_outfits")
        .update({ is_saved_for_later: false, worn_at: now } as never)
        .eq("id", outfit.id);
      const itemIds = outfit.wardrobe_item_ids ?? [];
      if (itemIds.length > 0) {
        await Promise.all(
          itemIds.map((id) => supabase.rpc("increment" as never, { row_id: id } as never)),
        );
      }
      setWornOutfits((prev) =>
        prev.map((o) =>
          o.id === outfit.id ? { ...o, is_saved_for_later: false, worn_at: now } : o,
        ),
      );
      setWardrobeItems((prev) =>
        prev.map((item) =>
          itemIds.includes(item.id)
            ? { ...item, last_used_at: now, times_used: (item.times_used ?? 0) + 1 }
            : item,
        ),
      );
      trackEvent("wardrobe_saved_look_worn", {
        occasion: outfit.occasion,
        days_saved: outfit.created_at
          ? Math.floor((Date.now() - new Date(outfit.created_at).getTime()) / (1000 * 60 * 60 * 24))
          : 0,
      });
      setCurrentWornOutfitId(outfit.id);
      setTimeout(() => setShowSentiment(true), 800);
    } catch (err) {
      console.error("Wear saved look failed:", err);
    }
  };

  // Render a worn outfit card (image or colour grid)
  const renderWornCard = (o: WornOutfit, width: number, height: number) => {
    const colours = o.wardrobe_item_ids
      .map((id) => wornById.get(id)?.color_hex)
      .filter((c): c is string => !!c)
      .slice(0, 4);
    return (
      <div style={{ width, height, borderRadius: 12, overflow: "hidden", flexShrink: 0, background: "#F4F4F5", border: "0.5px solid #E5E7EB" }}>
        <div style={{ height: "70%", width: "100%", position: "relative", background: "#E5E7EB" }}>
          {o.generated_image_url ? (
            <img  loading="lazy" decoding="async"src={o.generated_image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(2, Math.max(1, colours.length))}, 1fr)`, gridAutoRows: "1fr", width: "100%", height: "100%" }}>
              {(colours.length ? colours : ["#E5E7EB"]).map((c, i) => (
                <div key={i} style={{ background: c }} />
              ))}
            </div>
          )}
        </div>
        <div style={{ height: "30%", background: "#F4F4F5", padding: "7px 12px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 2 }}>
          {o.occasion && (
            <span style={{ background: "white", borderRadius: 999, padding: "3px 8px", fontSize: 12, color: "#6B7280", alignSelf: "flex-start", textTransform: "capitalize" }}>
              {o.occasion.replace(/_/g, " ")}
            </span>
          )}
          <span style={{ fontSize: 12, color: "#9CA3AF" }}>{o.worn_at ? formatShortDate(o.worn_at) : "Saved"}</span>
        </div>
      </div>
    );
  };





  if (isProcessingItems || processingComplete) {
    const doneOrErrCount = processingItems.filter((i) => i.status === "done" || i.status === "error").length;
    const doneCount = processingItems.filter((i) => i.status === "done").length;
    const errCount = processingItems.filter((i) => i.status === "error").length;
    const total = processingItems.length;
    return (
      <div className="flex-1 flex flex-col px-4 pt-10 pb-24">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 className="text-2xl font-extrabold tracking-tight">
            {processingComplete
              ? `${doneCount} item${doneCount === 1 ? "" : "s"} added`
              : `Adding ${total} item${total === 1 ? "" : "s"}`}
          </h2>
          {!processingComplete && (
            <p style={{ fontSize: 12, color: "#6B7280", margin: 0 }}>
              {doneOrErrCount} of {total}
            </p>
          )}
        </div>

        {!processingComplete && (
          <div style={{ width: "100%", background: "#F4F4F5", borderRadius: 999, height: 6, marginBottom: 16, overflow: "hidden" }}>
            <div
              style={{
                width: `${(doneOrErrCount / total) * 100}%`,
                background: CORAL,
                height: 6,
                borderRadius: 999,
                transition: "width 0.4s ease",
              }}
            />
          </div>
        )}

        {processingComplete && (
          <div style={{ background: "#FAECE7", borderRadius: 12, padding: 12, border: "0.5px solid #F5C4B3", display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <Check size={20} color="#D85A30" />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 13, fontWeight: 500, color: "#712B13", margin: 0 }}>
                {doneCount} item{doneCount === 1 ? "" : "s"} added to your wardrobe
              </p>
              {errCount > 0 && (
                <p style={{ fontSize: 12, color: "#993C1D", margin: "2px 0 0" }}>
                  {errCount} photo{errCount === 1 ? "" : "s"} could not be detected
                </p>
              )}
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          {processingItems.map((item) => (
            <div
              key={item.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: 12,
                borderRadius: 12,
                border: "0.5px solid #E5E7EB",
                background: "white",
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: item.color_hex ?? "#F4F4F5",
                  border: "0.5px solid #E5E7EB",
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                {item.status === "waiting" && (
                  <p style={{ fontSize: 13, color: "#6B7280", margin: 0 }}>Waiting</p>
                )}
                {item.status === "processing" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <p style={{ fontSize: 13, color: "#111", margin: 0, fontWeight: 500 }}>Detecting</p>
                    <div style={{ display: "inline-flex", gap: 3 }}>
                      <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#6B7280", animation: "dot-pulse 1.2s ease-in-out infinite", animationDelay: "0s" }} />
                      <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#6B7280", animation: "dot-pulse 1.2s ease-in-out infinite", animationDelay: "0.2s" }} />
                      <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#6B7280", animation: "dot-pulse 1.2s ease-in-out infinite", animationDelay: "0.4s" }} />
                    </div>
                  </div>
                )}
                {item.status === "done" && (
                  <>
                    <p style={{ fontSize: 13, fontWeight: 500, color: "#111", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.name}
                    </p>
                    <p style={{ fontSize: 12, color: "#6B7280", margin: 0, textTransform: "capitalize" }}>
                      {item.category}
                    </p>
                  </>
                )}
                {item.status === "error" && (
                  <>
                    <p style={{ fontSize: 13, fontWeight: 500, color: "#111", margin: 0 }}>Could not detect</p>
                    <p style={{ fontSize: 12, color: "#6B7280", margin: 0 }}>Try a clearer photo</p>
                  </>
                )}
              </div>
              {item.status === "done" && <Check size={18} color="#10B981" />}
              {item.status === "error" && <X size={18} color="#9CA3AF" />}
            </div>
          ))}
        </div>

        {processingComplete && (
          <button
            onClick={() => {
              setIsProcessingItems(false);
              setProcessingComplete(false);
              setProcessingItems([]);
            }}
            style={{
              background: CORAL,
              border: "none",
              borderRadius: 999,
              padding: "12px",
              fontSize: 14,
              fontWeight: 500,
              color: "white",
              cursor: "pointer",
              width: "100%",
              marginTop: 8,
            }}
          >
            Done
          </button>
        )}
      </div>
    );
  }

  const weatherTempC = WEATHER_TO_TEMP_C[selectedWeather] ?? null;

  const isStyleYourselfView = drawerRoute === "style-yourself";
  return (
    <div
      className="flex-1 flex flex-col l2f-wardrobe-root"
      style={{
        background: isStyleYourselfView ? "#FFFCF8" : "#E8E0D5",
        minHeight: "100dvh",
        paddingTop: "calc(var(--safe-top, 0px) + 12px)",
        paddingBottom: "calc(var(--safe-bottom, 0px) + 78px)",
      }}
    >
      <input ref={singleFileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleItemPhotosSelected} />
      <input ref={outfitFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleOutfitPhoto(e.target.files?.[0])} />

      {/* Wardrobe error banner */}
      {wardrobeError && isStyleYourselfView && (
        <div style={{ background: "#C84B2F", color: "white", padding: "8px 20px", fontSize: 12, textAlign: "center", borderRadius: 12, marginBottom: 8 }}>
          {wardrobeError}
        </div>
      )}

      {/* Top header — only for non-style-yourself routes; StyleYourself owns its own header */}
      {drawerRoute === "items" ? (
        <div style={{ padding: "14px 6px 6px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              type="button"
              onClick={() => setDrawerRoute("style-yourself")}
              aria-label="Back"
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: "#F1EFE8",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <i className="ti ti-arrow-left" style={{ fontSize: 16, color: "#2C2820" }} aria-hidden="true" />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontSize: 12,
                  color: "#C84B2F",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  margin: "0 0 2px",
                  fontWeight: 500,
                }}
              >
                LIKE2FIT
              </p>
              <p
                style={{
                  fontFamily: "var(--font-serif, var(--font-display))",
                  fontStyle: "italic",
                  fontSize: 18,
                  color: "#2C2820",
                  margin: 0,
                  lineHeight: 1.1,
                }}
              >
                Your items
              </p>
            </div>
            <span
              className="l2f-type-caption"
              style={{
                background: "rgba(255,255,255,0.55)",
                border: "1px solid rgba(0,0,0,0.07)",
                color: "#4A4540",
                borderRadius: 22,
                padding: "5px 11px",
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {wardrobeItems.length} items
            </span>
          </div>
        </div>
      ) : drawerRoute === "history" || drawerRoute === "insights" ? (
        <div style={{ display: "flex", alignItems: "center", padding: "14px 20px 8px" }}>
          <button
            type="button"
            onClick={() => setDrawerRoute("style-yourself")}
            aria-label="Back"
            style={{ background: "transparent", border: "none", padding: 0, marginRight: 12, cursor: "pointer" }}
          >
            <i className="ti ti-arrow-left" style={{ fontSize: 18, color: "#3C3A36" }} aria-hidden="true" />
          </button>
          <h2 style={{ fontSize: 16, fontWeight: 500, color: "#2C2C2A", margin: 0 }}>
            {drawerRoute === "history" ? "History" : "Insights"}
          </h2>
        </div>
      ) : null}

      {/* Style Yourself combinator (default home) */}
      {isStyleYourselfView && (
        wardrobeItems.length < 5 ? (
          <EmptyState
            count={wardrobeItems.length}
            onAddItem={() => setAddSheetOpen(true)}
          />
        ) : wardrobeGenerating ? (
          <GeneratingState
            caption="Pulling the look together"
            gender={userProfile?.model_gender ?? userProfile?.gender ?? null}
          />
        ) : (
          // Picker renders in natural flow; its FAB is position:fixed and
          // tracks viewport. Wrapper is no longer needed here — the result
          // branch inside StyleYourself applies its own bounded wrapper
          // when needed so its sticky footer pins.
          <StyleYourself
            userId={userId}
            wardrobeItems={wardrobeItems.map((i) => ({
              id: i.id,
              name: i.name,
              category: i.category,
              color_hex: i.color_hex,
              image_url: i.image_url,
              image_path: (i as { image_path?: string | null }).image_path ?? null,
              cutout_path: (i as { cutout_path?: string | null }).cutout_path ?? null,
              cutout_status: (i as { cutout_status?: string | null }).cutout_status ?? null,
              pattern: (i as { pattern?: string | null }).pattern ?? null,
              style_tags: (i as { style_tags?: string[] | null }).style_tags ?? null,
              vision_description: (i as { vision_description?: string | null }).vision_description ?? null,
              lb3_description: (i as { lb3_description?: string | null }).lb3_description ?? null,
              lb4_description: (i as { lb4_description?: string | null }).lb4_description ?? null,
              lb4_eligible: (i as { lb4_eligible?: boolean | null }).lb4_eligible ?? null,
              lb3_eligible: (i as { lb3_eligible?: boolean | null }).lb3_eligible ?? null,
              last_used_at: (i as { last_used_at?: string | null }).last_used_at ?? null,
              times_used: (i as { times_used?: number | null }).times_used ?? null,
            }))}
            occasion={selectedOccasion}
            weather={selectedWeather}
            mood={selectedMood}
            gender={userProfile?.model_gender ?? userProfile?.gender ?? null}
            modelSetup={buildModelSetup()}
            modelProfile={{
              model_gender: userProfile?.model_gender ?? null,
              model_height: userProfile?.model_height ?? null,
              model_body_shape: userProfile?.model_body_shape ?? null,
              model_body_size: userProfile?.model_body_size ?? null,
              model_skin_tone: userProfile?.model_skin_tone ?? null,
              model_hair: userProfile?.model_hair ?? null,
            }}
            onClose={() => setDrawerRoute("items")}
            onOpenDrawer={() => setDrawerOpen(true)}
          />
        )
      )}

      {drawerRoute === "saved-looks" && userId && (
        savedView ? (
          <div
            className="flex flex-col"
            style={{
              height:
                "calc(100dvh - var(--safe-top, 0px) - var(--safe-bottom, 0px) - 12px - 78px)",
            }}
          >
            <StyleYourselfResult
              outfitId={savedView.outfitId}
              imageUrl={savedView.lookbookImageUrl}
              loading={false}
              modelProfile={{
                model_gender: userProfile?.model_gender ?? null,
                model_height: userProfile?.model_height ?? null,
                model_body_shape: userProfile?.model_body_shape ?? null,
                model_body_size: userProfile?.model_body_size ?? null,
                model_skin_tone: userProfile?.model_skin_tone ?? null,
                model_hair: userProfile?.model_hair ?? null,
              }}
              onBack={() => setSavedView(null)}
              onTryAnother={() => {}}
              onTweakCombo={() => {}}
              initiallySaved={true}
              hideTryAnother={true}
            />
          </div>
        ) : (
          <SavedLooks
            userId={userId}
            onClose={() => setDrawerRoute("style-yourself")}
            onOpenDrawer={() => setDrawerOpen(true)}
            onTapCard={(look) =>
              setSavedView({
                outfitId: look.id,
                lookbookImageUrl: look.lookbook_image_url,
              })
            }
            onEmptyCta={() => setDrawerRoute("style-yourself")}
          />
        )
      )}





      {drawerRoute === "items" && (
        <>


      {count < 5 ? (
        <div
          style={{
            background: "#FAECE7",
            borderRadius: 12,
            padding: 12,
            border: "0.5px solid #F5C4B3",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <Archive size={28} color="#D85A30" />
          <p style={{ fontSize: 12, fontWeight: 500, color: "#712B13", margin: 0, textAlign: "center" }}>
            {count === 0 ? "Your wardrobe is empty" : `${count} item${count === 1 ? "" : "s"} added — keep going!`}
          </p>
          <p style={{ fontSize: 12, color: "#993C1D", margin: 0, textAlign: "center" }}>
            Add {5 - count} more item{5 - count === 1 ? "" : "s"} to generate your first look
          </p>
          <div style={{ width: "100%", background: "#F5C4B3", borderRadius: 999, height: 4 }}>
            <div
              style={{
                width: `${(count / 5) * 100}%`,
                background: "#FF5C5C",
                height: 4,
                borderRadius: 999,
                transition: "width 0.3s ease",
              }}
            />
          </div>
          <p style={{ fontSize: 12, color: "#993C1D", margin: 0 }}>{count} / 5 items</p>
        </div>
      ) : (
        <div ref={generateRef} style={{ background: "#F4F4F5", borderRadius: 12, padding: 12, border: "0.5px solid #E5E7EB", marginBottom: 12 }}>

          {lookbookUrl ? (
            <div>
              <img  loading="lazy" decoding="async"src={lookbookUrl} alt="Today's look" style={{ width: "100%", borderRadius: 12, marginBottom: 10 }} />
              <p style={{ fontSize: 13, fontWeight: 500, margin: "4px 0 2px" }}>Today's Look</p>
              {styleNote && <p style={{ fontSize: 12, color: "#6B7280", margin: "0 0 10px" }}>{styleNote}</p>}
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <p style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)", margin: 0, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Items used
                  </p>
                  <button
                    onClick={() => openCarouselAt(0)}
                    style={{ background: "transparent", border: "none", fontSize: 12, color: "#FF5C5C", cursor: "pointer", padding: 1, fontWeight: 500 }}
                  >
                    View all photos →
                  </button>
                </div>

                {selectedLookItems.map((it, index) => {
                  const under = isUnderused(it);
                  return (
                    <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 8, background: under ? AMBER_BG : "white", borderRadius: 10, padding: "6px 8px", border: "0.5px solid #E5E7EB" }}>
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <div
                          onClick={() => openCarouselAt(index)}
                          style={{ width: 28, height: 28, borderRadius: 8, background: it.color_hex ?? "#E5E7EB", overflow: "hidden", flexShrink: 0, cursor: "pointer" }}
                        >
                          <WardrobeImg item={it} transformWidth={120} transformQuality={80} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                        <div style={{
                          position: "absolute",
                          bottom: 3,
                          right: 3,
                          background: "rgba(0,0,0,0.45)",
                          borderRadius: 4,
                          width: 16,
                          height: 16,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>
                          <i
                            className="ti ti-arrows-maximize"
                            style={{
                              fontSize: 12,
                              color: "white"
                            }}
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 12, fontWeight: 500, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {it.name}{under && <span style={{ color: AMBER, fontWeight: 500 }}> ✨ try this</span>}
                        </p>
                        <p style={{ fontSize: 12, color: "#6B7280", margin: 0 }}>{lastWornLabel(it)}</p>
                      </div>
                      <button onClick={() => setSwapTarget(it)} className="text-xs font-medium px-2 py-1 rounded-full" style={{ border: "1px solid #E5E7EB", color: "#111" }}>Swap</button>
                    </div>
                  );
                })}
              </div>
              {hasUnsavedSwap && (
                <button onClick={handleRegenerate} disabled={wardrobeGenerating} className="w-full mb-2 py-2.5 rounded-full text-xs font-medium flex items-center justify-center gap-1.5" style={{ background: "white", border: `1px solid ${CORAL}`, color: CORAL }}>
                  <RefreshCw size={14} /> Regenerate look with new items
                </button>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <button onClick={handleWoreThis} disabled={wornSaved} className="l2f-btn-primary">
                  {wornSaved ? "Saved ✓" : "I wore this today"}
                </button>
                <button onClick={handleSaveForLater} className="l2f-btn-ghost">
                  Save for later
                </button>
              </div>
              {showSentiment && (
                <div style={{ marginTop: 12, padding: 14, background: "var(--color-background-secondary, #FAFAFA)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 14, display: "flex", flexDirection: "column", gap: 12, animation: "slideUp 0.3s ease-out" }}>
                  {sentimentSubmitted ? (
                    <p style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: 0, textAlign: "center" }}>
                      Thanks — this helps us improve your suggestions
                    </p>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, fontWeight: 500, margin: 0, textAlign: "center" }}>
                        How did you feel in this look?
                      </p>
                      <div style={{ display: "flex", gap: 8 }}>
                        {([
                          { value: "loved", emoji: "😍", label: "Loved it" },
                          { value: "good", emoji: "😊", label: "It was good" },
                          { value: "okay", emoji: "😐", label: "It was okay" },
                        ] as const).map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => handleSentiment(opt.value)}
                            style={{ flex: 1, background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 10, padding: "10px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer" }}
                          >
                            <span style={{ fontSize: 22 }}>{opt.emoji}</span>
                            <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{opt.label}</span>
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => { setShowSentiment(false); setSentimentSubmitted(true); }}
                        style={{ background: "transparent", border: "none", fontSize: 12, color: "var(--color-text-tertiary)", cursor: "pointer", padding: 0, textAlign: "center" }}
                      >
                        Skip
                      </button>
                    </>
                  )}
                </div>
              )}
              {onGoToShopping && (
                <button onClick={handleShopLook} className="w-full mt-2 py-2.5 rounded-full text-xs font-medium" style={{ border: `1px solid ${CORAL}`, color: CORAL, background: "white" }}>
                  Get The Look →
                </button>
              )}
              <button onClick={() => { setLookbookUrl(null); setSelectedLookItems([]); setStyleNote(""); setGenOutfitId(null); }} className="w-full mt-2 text-xs text-muted-foreground">Start over</button>
            </div>
          ) : wardrobeError ? (
            <div style={{ background: "#FAECE7", borderRadius: 12, padding: "14px 16px", border: "0.5px solid #F5C4B3", display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 14, fontWeight: 500, color: "#712B13", margin: 0 }}>Could not generate your look</p>
              <p style={{ fontSize: 12, color: "#993C1D", margin: 0, lineHeight: 1.5 }}>{wardrobeError}</p>
              <button onClick={() => { setWardrobeError(null); }} style={{ background: "#FF5C5C", border: "none", borderRadius: 999, padding: "10px", fontSize: 13, fontWeight: 500, color: "white", cursor: "pointer" }}>
                Try again
              </button>
            </div>
          ) : wardrobeGenerating ? (
            <div style={{ background: "var(--color-background-primary)", borderRadius: 16, border: "0.5px solid var(--color-border-tertiary)", overflow: "hidden" }}>
              <div style={{ background: "#FAECE7", padding: "20px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "white", border: "1.5px solid #F0997B", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <i className="ti ti-shirt" style={{ fontSize: 22, color: "#D85A30" }} aria-hidden="true" />
                </div>
                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ width: "100%", background: "#F5C4B3", borderRadius: 999, height: 5, overflow: "hidden" }}>
                    <div style={{ height: 5, borderRadius: 999, background: "#FF5C5C", width: `${progressWidth}%`, transition: "width 0.6s ease" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <p style={{ fontSize: 12, color: "#993C1D", margin: 0 }}>{progressLabel}</p>
                    <p style={{ fontSize: 12, color: "#D85A30", margin: 0 }}>60–90 seconds</p>
                  </div>
                </div>
                <div style={{ background: "white", borderRadius: 10, padding: "10px 14px", width: "100%", border: "0.5px solid #F5C4B3" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <i className="ti ti-sparkles" style={{ fontSize: 13, color: "#D85A30" }} aria-hidden="true" />
                    <p style={{ fontSize: 12, fontWeight: 500, color: "#712B13", margin: 0 }}>Styling your wardrobe</p>
                  </div>
                  <p style={{ fontSize: 12, color: "#993C1D", margin: 0, lineHeight: 1.5 }}>
                    Selecting items for a {selectedOccasion} look in {selectedWeather} weather
                  </p>
                </div>
              </div>
              <div style={{ padding: "10px 14px", background: "var(--color-background-primary)", borderTop: "0.5px solid var(--color-border-tertiary)", display: "flex", gap: 8, alignItems: "center", overflow: "hidden" }}>
                {wardrobeItems.slice(0, 4).map((item) => (
                  <div key={item.id} style={{ width: 44, height: 52, borderRadius: 8, background: item.color_hex ?? "var(--color-background-tertiary)", flexShrink: 0, opacity: 0.5, border: "0.5px solid var(--color-border-tertiary)" }} />
                ))}
                <p style={{ fontSize: 12, color: "var(--color-text-tertiary)", margin: 0, flexShrink: 0 }}>Considering your wardrobe</p>
              </div>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 12, fontWeight: 500, margin: "0 0 6px" }}>What are you wearing today?</p>
              <p className="l2f-section-label">Occasion</p>
              <div className="l2f-pill-group">
                {OCCASIONS.map((o) => (
                  <button
                    key={o.key}
                    onClick={() => setSelectedOccasion(o.key)}
                    className={`l2f-pill ${selectedOccasion === o.key ? "l2f-pill-active" : ""}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <p className="l2f-section-label">Weather</p>
              <div className="l2f-pill-group" style={{ marginBottom: lastKnownCity ? 4 : undefined }}>
                {WEATHERS.map((o) => (
                  <button
                    key={o.key}
                    onClick={() => setSelectedWeather(o.key)}
                    className={`l2f-pill ${selectedWeather === o.key ? "l2f-pill-active" : ""}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {lastKnownCity && (
                <p style={{ fontSize: 12, color: "#6B7280", margin: "0 0 8px" }}>
                  Auto-detected for {lastKnownCity}
                  {" · "}
                  <button
                    onClick={() => {
                      setSelectedWeather("cool");
                      setLastKnownCity(null);
                    }}
                    style={{ background: "transparent", border: "none", fontSize: 12, color: "#FF5C5C", cursor: "pointer", padding: 0 }}
                  >
                    Change
                  </button>
                </p>
              )}
              <p className="l2f-section-label">Mood</p>
              <div className="l2f-pill-group">
                {moodOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedMood(option.value as MoodKey)}
                    className={`l2f-pill ${selectedMood === option.value ? "l2f-pill-active" : ""}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setDrawerRoute("style-yourself")}
                disabled={wardrobeItems.length < 5}
                className="l2f-btn-primary"
              >
                <Sparkles size={14} /> Style me ✨
              </button>
              {genError && <p style={{ fontSize: 12, color: "#DC2626", margin: "6px 0 0", textAlign: "center" }}>Something went wrong. Try again.</p>}
            </>
          )}
        </div>
      )}

      {/* Insights card */}
      {wardrobeInsights && wardrobeItems.length >= 5 && wornOutfits.length >= 1 && (
        <div style={{
          background: "#F4F4F5",
          borderRadius: 12,
          padding: "8px 12px",
          border: "0.5px solid #E5E7EB",
          marginBottom: 8,
        }}>
          <p style={{ fontSize: 12, color: "#374151", margin: 0 }}>
            {insightIndex === 0 && wardrobeInsights.underusedCount > 0 && (
              <>✨ {wardrobeInsights.underusedCount} item{wardrobeInsights.underusedCount === 1 ? "" : "s"} waiting to be worn
              {wardrobeInsights.mostUsedItem && ` · Most worn: ${wardrobeInsights.mostUsedItem.name}`}</>
            )}
            {insightIndex === 1 && (
              <>{wardrobeInsights.totalOutfits} look{wardrobeInsights.totalOutfits === 1 ? "" : "s"} styled from your wardrobe
              {wardrobeInsights.topOccasion && ` · Top occasion: ${wardrobeInsights.topOccasion.replace(/_/g, " ")}`}</>
            )}
            {insightIndex === 2 && wardrobeInsights.mostUsedItem && (wardrobeInsights.mostUsedItem.times_used ?? 0) > 0 && (
              <>Your {wardrobeInsights.mostUsedItem.name} has been worn {wardrobeInsights.mostUsedItem.times_used} time{wardrobeInsights.mostUsedItem.times_used === 1 ? "" : "s"} — a true wardrobe hero</>
            )}
            {((insightIndex === 0 && wardrobeInsights.underusedCount === 0) ||
              (insightIndex === 2 && (!wardrobeInsights.mostUsedItem || (wardrobeInsights.mostUsedItem.times_used ?? 0) === 0))) && (
              <>{wardrobeInsights.totalOutfits} look{wardrobeInsights.totalOutfits === 1 ? "" : "s"} styled from your wardrobe</>
            )}
          </p>
        </div>
      )}

      {/* Needs some love */}
      {shouldShowUnderusedDots && underusedItems.length >= 2 && (
        <div style={{ marginBottom: 12 }}>
          <p className="l2f-section-label" style={{ fontFamily: "var(--font-body)", letterSpacing: "0.12em", margin: "0 2px 2px" }}>Needs some love</p>
          <p style={{ fontSize: 12, color: "#6B7280", margin: "0 2px 6px" }}>Items you haven't worn recently</p>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
            {underusedItems.slice(0, 12).map((it) => {
              const d = daysSinceWorn(it);
              return (
                <button
                  key={it.id}
                  onClick={() => setUnderusedSuggest(it)}
                  style={{ flexShrink: 0, width: 64, textAlign: "left" }}
                >
                  <div style={{
                    width: 64, height: 64, borderRadius: 10,
                    background: it.color_hex ?? "#F4F4F5",
                    border: `1.5px solid ${AMBER}`,
                    overflow: "hidden",
                  }}>
                    <WardrobeImg item={it} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                  <p style={{ fontSize: 12, color: "#854F0B", margin: "3px 0 0", lineHeight: 1.2 }}>
                    {d === null ? "Never worn" : `Not worn in ${d} days`}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}




      {showRegenerateFeedback && (
        <>
          <div onClick={() => setShowRegenerateFeedback(false)} style={{ position: "fixed", inset: 0, background: "rgba(44,40,32,0.4)", zIndex: 90 }} />
          <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", left: 0, right: 0, bottom: "calc(78px + var(--safe-bottom, 0px))", background: "#FFFCF8", borderRadius: "22px 22px 0 0", maxHeight: "80vh", overflowY: "auto", zIndex: 100, boxShadow: "0 -10px 24px rgba(0,0,0,0.18)" }}>
            <div style={{ padding: "8px 0", display: "flex", justifyContent: "center" }}>
              <div style={{ width: 36, height: 4, background: "#E0D8D0", borderRadius: 2 }} />
            </div>
            <div style={{ padding: "0 16px 6px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div className="l2f-type-section-header" style={{ color: "#9B958E" }}>QUICK FEEDBACK</div>
                <div style={{ fontSize: 18, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", lineHeight: 1.1, marginTop: 1 }}>What wasn't quite right?</div>
              </div>
              <button onClick={() => setShowRegenerateFeedback(false)} aria-label="Close" style={{ width: 32, height: 32, borderRadius: "50%", background: "#F1EFE8", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                <i className="ti ti-x" style={{ fontSize: 14, color: "#4A4540" }} aria-hidden="true" />
              </button>
            </div>
            <div style={{ padding: "0 16px 6px", fontSize: 12, color: "#6B6560" }}>Optional — helps us tune your looks</div>
            <div style={{ padding: "14px 16px 8px" }}>
              {([
                { value: "wrong_colours", label: "Wrong colours" },
                { value: "wrong_occasion", label: "Wrong for the occasion" },
                { value: "wrong_combination", label: "Don't like the combination" },
                { value: "want_different", label: "Just want something different" },
              ] as const).map((opt, idx, arr) => (
                <button
                  key={opt.value}
                  onClick={() => { triggerHaptic("selection"); handleRegenerateFeedback(opt.value); }}
                  style={{ display: "block", width: "100%", padding: "12px 14px", background: "#FFFCF8", border: "1px solid #E0D8D0", borderRadius: 14, fontSize: 13, color: "#2C2820", textAlign: "left", marginBottom: idx === arr.length - 1 ? 0 : 8, cursor: "pointer" }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div style={{ padding: "4px 16px 16px" }}>
              <button
                onClick={() => { triggerHaptic("selection"); setShowRegenerateFeedback(false); setGenerateTappedAt(null); handleGenerate(); }}
                style={{ width: "100%", padding: 11, background: "#C84B2F", color: "#fff", border: "none", borderRadius: 22, fontSize: 13, fontWeight: 500, cursor: "pointer" }}
              >
                Skip — just regenerate
              </button>
            </div>
          </div>
        </>
      )}

      {swapTarget && (
        <>
          <div onClick={() => setSwapTarget(null)} style={{ position: "fixed", inset: 0, background: "rgba(44,40,32,0.4)", zIndex: 90 }} />
          <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", left: 0, right: 0, bottom: "calc(78px + var(--safe-bottom, 0px))", background: "#FFFCF8", borderRadius: "22px 22px 0 0", maxHeight: "80vh", display: "flex", flexDirection: "column", zIndex: 100, boxShadow: "0 -10px 24px rgba(0,0,0,0.18)" }}>
            <div style={{ padding: "8px 0", display: "flex", justifyContent: "center", flexShrink: 0 }}>
              <div style={{ width: 36, height: 4, background: "#E0D8D0", borderRadius: 2 }} />
            </div>
            <div style={{ padding: "0 16px 6px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="l2f-type-section-header" style={{ color: "#9B958E" }}>SWAP ITEM</div>
                <div style={{ fontSize: 18, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", lineHeight: 1.1, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{swapTarget.name} → ?</div>
              </div>
              <button onClick={() => setSwapTarget(null)} aria-label="Close" style={{ width: 32, height: 32, borderRadius: "50%", background: "#F1EFE8", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                <i className="ti ti-x" style={{ fontSize: 14, color: "#4A4540" }} aria-hidden="true" />
              </button>
            </div>
            <div style={{ padding: "0 16px 8px", fontSize: 12, color: "#6B6560", flexShrink: 0 }}>Pick another {swapTarget.category} from your wardrobe</div>
            <div style={{ flex: 1, overflowY: "auto", maxHeight: 320 }}>
              {swapOptions.length === 0 ? (
                <div style={{ padding: "32px 16px", textAlign: "center" }}>
                  <i className="ti ti-hanger" style={{ fontSize: 28, color: "#9B958E" }} aria-hidden="true" />
                  <div style={{ fontSize: 13, fontStyle: "italic", color: "#6B6560", fontFamily: "var(--font-serif)", marginTop: 8 }}>No other {swapTarget.category} in your wardrobe</div>
                  <div style={{ fontSize: 12, color: "#9B958E", marginTop: 4 }}>Add more items to enable swapping</div>
                </div>
              ) : swapOptions.map((opt, idx) => {
                const orig = swapTarget;
                return (
                  <button
                    key={opt.id}
                    onClick={() => {
                      triggerHaptic("success");
                      performSwap(opt);
                      toast.success(`Swapped to ${opt.name}`, {
                        action: {
                          label: "Undo",
                          onClick: () => {
                            setSelectedLookItems((prev) => prev.map((i) => (i.id === opt.id ? orig : i)));
                          },
                        },
                      });
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "10px 14px", background: "transparent", border: "none", textAlign: "left", borderBottom: idx === swapOptions.length - 1 ? "none" : "0.5px solid #F1EFE8", cursor: "pointer" }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: opt.cutout_status === "ready" ? "var(--surface-2)" : (opt.color_hex ?? "#E5E7EB"), overflow: "hidden", flexShrink: 0 }}>
                      <WardrobeImg item={opt} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: "#2C2820", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.name}</div>
                      <div style={{ fontSize: 12, color: "#9B958E", marginTop: 1 }}>{lastWornLabel(opt)}</div>
                    </div>
                    <i className="ti ti-arrow-right" style={{ fontSize: 14, color: "#C84B2F", flexShrink: 0 }} aria-hidden="true" />
                  </button>
                );
              })}
            </div>
            {swapOptions.length > 0 && (
              <div style={{ padding: "14px 16px calc(var(--safe-bottom, 0px) + 14px)", textAlign: "center", fontSize: 12, color: "#9B958E", fontStyle: "italic", fontFamily: "var(--font-serif)", flexShrink: 0 }}>
                Tap an item to swap
              </div>
            )}
          </div>
        </>
      )}

      {(() => {
        const CATEGORY_DEFS: Array<{ key: string; label: string; match: (c: string) => boolean }> = [
          { key: "all", label: "All", match: () => true },
          { key: "top", label: "Tops", match: (c) => c === "top" },
          { key: "bottom", label: "Bottoms", match: (c) => c === "bottom" },
          { key: "dress", label: "Dresses", match: (c) => c === "dress" },
          { key: "outerwear", label: "Outerwear", match: (c) => c === "outerwear" },
          { key: "shoes", label: "Shoes", match: (c) => c === "shoes" },
          { key: "bag", label: "Bags", match: (c) => c === "bag" },
          { key: "accessory", label: "Accessories", match: (c) => c === "accessory" },
          { key: "other", label: "Other", match: (c) => c === "other" },
        ];
        const presentCats = new Set(wardrobeItems.map((i) => i.category));
        const chips = CATEGORY_DEFS.filter((d) => d.key === "all" || presentCats.has(d.key));
        const filtered = activeCategory === "all"
          ? wardrobeItems
          : wardrobeItems.filter((i) => i.category === activeCategory);

        return (
          <>
            <div
              style={{
                display: "flex",
                gap: 6,
                overflowX: "auto",
                paddingBottom: 4,
                margin: "10px 6px",
                scrollbarWidth: "none",
              }}
            >
              {chips.map((c) => {
                const catCount = c.key === "all"
                  ? wardrobeItems.length
                  : wardrobeItems.filter((i) => i.category === c.key).length;
                const active = activeCategory === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => {
                      setActiveCategory(c.key);
                      trackEvent("wardrobe_items_filter_changed", { category: c.key });
                    }}
                    className="l2f-type-caption"
                    style={{
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      background: active ? "#2C2820" : "rgba(255,255,255,0.55)",
                      color: active ? "#F5F0EB" : "#4A4540",
                      border: active ? "1px solid transparent" : "1px solid rgba(0,0,0,0.07)",
                      borderRadius: 22,
                      padding: "5px 11px",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    {c.label} · {catCount}
                  </button>
                );
              })}
            </div>

            {filtered.length === 0 ? (
              <div style={{ padding: "24px 16px", textAlign: "center" }}>
                <p className="l2f-type-caption" style={{ color: "#6B6560", margin: "0 0 6px" }}>
                  No items in this category yet
                </p>
                <button
                  type="button"
                  onClick={() => setActiveCategory("all")}
                  className="l2f-type-caption"
                  style={{ background: "transparent", border: "none", color: "#C84B2F", fontWeight: 500, cursor: "pointer" }}
                >
                  Show all
                </button>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, padding: "0 6px" }}>
                {filtered.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActionItem(item)}
                    style={{
                      background: "#FFFCF8",
                      border: "0.5px solid #E8E2D9",
                      borderRadius: 20,
                      padding: 0,
                      overflow: "hidden",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ position: "relative", aspectRatio: "1", background: item.cutout_status === "ready" ? "var(--surface-2)" : (item.color_hex ?? "var(--l2f-surface-1)") }}>
                      <WardrobeImg item={item} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      {shouldShowUnderusedDots && isUnderused(item) && (
                        <div style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: "#EF9F27",
                          border: "1.5px solid white",
                        }} />
                      )}
                    </div>
                    <p
                      style={{
                        fontFamily: "var(--font-display)",
                        fontStyle: "italic",
                        fontSize: 12,
                        color: "#2C2820",
                        margin: 0,
                        padding: "6px 8px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.name}
                    </p>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setAddSheetOpen(true)}
                  style={{
                    background: "transparent",
                    border: "1.5px dashed #C84B2F",
                    borderRadius: 20,
                    aspectRatio: "1",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                    cursor: "pointer",
                    color: "#C84B2F",
                  }}
                >
                  <i className="ti ti-plus" style={{ fontSize: 18, color: "#C84B2F" }} aria-hidden="true" />
                  <span className="l2f-type-caption" style={{ fontWeight: 500, color: "#C84B2F" }}>Add item</span>
                </button>
              </div>
            )}
          </>
        );
      })()}
        </>
      )}

      {drawerRoute === "history" && (() => {
        const savedForLater = wornOutfits.filter((o) => o.is_saved_for_later);
        const wornLooks = wornOutfits.filter((o) => !o.is_saved_for_later);

        if (savedForLater.length === 0 && wornLooks.length === 0) {
          return (
            <div className="l2f-empty-state" style={{ background: "var(--l2f-cream)", borderRadius: 16, margin: "0 4px" }}>
              <p style={{ fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--l2f-coral)" }}>The Archive</p>
              <i className="ti ti-clock" style={{ fontSize: 28, color: "var(--color-text-tertiary, #9CA3AF)", marginTop: 8 }} aria-hidden="true" />
              <p className="l2f-empty-state-title">No history yet.</p>
              <p className="l2f-empty-state-subtitle">
                Generate a look and tap "I wore this today" or "Save for later"
              </p>
              <button
                onClick={() => setWardrobeTab("items")}
                className="l2f-btn-primary"
                style={{ width: "auto", padding: "8px 20px", fontSize: 12 }}
              >
                Generate a look →
              </button>
            </div>
          );
        }

        return (
          <div>
            {savedForLater.length > 0 && (
              <>
                <p className="l2f-section-label">
                  Saved for later · {savedForLater.length}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {savedForLater.map((outfit) => (
                    <div key={outfit.id} style={{ background: "var(--color-background-secondary, #F4F4F5)", borderRadius: 12, overflow: "hidden", border: "0.5px solid #F5C4B3" }}>
                      <div style={{ display: "flex", gap: 10, padding: 10 }}>
                        <div style={{
                          width: 52,
                          height: 64,
                          borderRadius: 8,
                          background: "#FF5C5C",
                          flexShrink: 0,
                          backgroundImage: outfit.generated_image_url ? `url(${outfit.generated_image_url})` : undefined,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          opacity: 0.8,
                        }} />
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span className="l2f-badge l2f-badge-danger">Saved</span>
                            <p style={{ fontSize: 12, color: "var(--color-text-tertiary, #9CA3AF)", margin: 0 }}>
                              {formatRelativeDate(outfit.worn_at ?? outfit.created_at)}
                            </p>
                          </div>
                          <p style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary, #111)", margin: 0, textTransform: "capitalize" }}>
                            {outfit.occasion} · {outfit.weather}
                          </p>
                          <p style={{ fontSize: 12, color: "var(--color-text-secondary, #6B7280)", margin: 0 }}>
                            {(outfit.wardrobe_item_ids?.length ?? 0)} items
                          </p>
                          <button
                            onClick={() => handleWearSavedLook(outfit)}
                            style={{ background: "#FF5C5C", border: "none", borderRadius: 999, padding: "6px 12px", fontSize: 12, fontWeight: 500, color: "white", cursor: "pointer", alignSelf: "flex-start", marginTop: 2 }}
                          >
                            Wear this today →
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {wornLooks.length > 0 && (
              <>
                <p className="l2f-section-label" style={{ marginTop: savedForLater.length > 0 ? 16 : 0 }}>
                  Worn · {wornLooks.length}
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {wornLooks.map((outfit) => (
                    <div
                      key={outfit.id}
                      onClick={() => setDetailOutfit(outfit)}
                      style={{ background: "var(--color-background-secondary, #F4F4F5)", borderRadius: 12, overflow: "hidden", border: "0.5px solid var(--color-border-tertiary, #E5E7EB)", cursor: "pointer" }}
                    >
                      <div style={{
                        height: 80,
                        background: "var(--color-background-tertiary, #E5E7EB)",
                        backgroundImage: outfit.generated_image_url ? `url(${outfit.generated_image_url})` : undefined,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }} />
                      <div style={{ padding: "6px 8px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <p style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-primary, #111)", margin: 0, textTransform: "capitalize" }}>
                            {outfit.occasion}
                          </p>
                          {outfit.sentiment && (
                            <span style={{ fontSize: 14 }}>
                              {outfit.sentiment === "loved" ? "😍" : outfit.sentiment === "good" ? "😊" : "😐"}
                            </span>
                          )}
                        </div>
                        <p style={{ fontSize: 12, color: "var(--color-text-tertiary, #9CA3AF)", margin: "2px 0 0" }}>
                          {formatRelativeDate(outfit.worn_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {drawerRoute === "insights" && (() => {
        const wornOnly = wornOutfits.filter((o) => !o.is_saved_for_later);

        if (wornOnly.length === 0) {
          return (
            <div className="l2f-empty-state" style={{ background: "var(--l2f-cream)", borderRadius: 16, margin: "0 4px" }}>
              <p style={{ fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--l2f-coral)" }}>Style Insights</p>
              <i className="ti ti-chart-bar" style={{ fontSize: 28, color: "var(--color-text-tertiary, #9CA3AF)", marginTop: 8 }} aria-hidden="true" />
              <p className="l2f-empty-state-title">No insights yet.</p>
              <p className="l2f-empty-state-subtitle">
                Generate a look and log what you wore to start building your style insights
              </p>
              <button
                onClick={() => setWardrobeTab("items")}
                className="l2f-btn-primary"
                style={{ width: "auto", padding: "8px 20px", fontSize: 12 }}
              >
                Generate a look →
              </button>
            </div>
          );
        }

        const wornItemIds = new Set(wornOnly.flatMap((o) => o.wardrobe_item_ids ?? []));
        const utilisationPct = wardrobeItems.length > 0
          ? Math.round((wornItemIds.size / wardrobeItems.length) * 100)
          : 0;
        const thisWeekLooks = wornOnly.filter((o) => {
          const ref = o.worn_at ?? o.created_at;
          if (!ref) return false;
          const days = (Date.now() - new Date(ref).getTime()) / (1000 * 60 * 60 * 24);
          return days <= 7;
        }).length;

        const presentCategories = new Set(wardrobeItems.map((i) => i.category));
        const expectedCategories = [
          { key: "shoes", label: "shoes", message: "Shoes complete every outfit" },
          { key: "bag", label: "bags", message: "A bag adds the finishing touch" },
          { key: "outerwear", label: "outerwear", message: "Great for layering in cooler weather" },
        ];
        const missingCategories = expectedCategories.filter((c) => !presentCategories.has(c.key));

        const itemWornCount: Record<string, number> = {};
        wornOnly.forEach((outfit) => {
          (outfit.wardrobe_item_ids ?? []).forEach((id) => {
            itemWornCount[id] = (itemWornCount[id] ?? 0) + 1;
          });
        });
        const topItems = Object.entries(itemWornCount)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([id, count]) => ({ item: wardrobeItems.find((i) => i.id === id), count }))
          .filter((x): x is { item: WardrobeItem; count: number } => !!x.item);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let streak = 0;
        const checkDate = new Date(today);
        while (true) {
          const hasOutfit = wornOnly.some((o) => {
            if (!o.worn_at) return false;
            const worn = new Date(o.worn_at);
            worn.setHours(0, 0, 0, 0);
            return worn.getTime() === checkDate.getTime();
          });
          if (!hasOutfit) break;
          streak++;
          checkDate.setDate(checkDate.getDate() - 1);
        }

        const occasionCounts: Record<string, number> = {};
        wornOnly.forEach((o) => {
          const occ = o.occasion ?? "casual";
          occasionCounts[occ] = (occasionCounts[occ] ?? 0) + 1;
        });
        const topOccasion = Object.entries(occasionCounts).sort((a, b) => b[1] - a[1])[0];

        const sectionHeader = (title: string, first: boolean) => (
          <p style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary, #6B7280)", margin: `${first ? 0 : 16}px 0 8px`, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {title}
          </p>
        );

        return (
          <div>
            {sectionHeader("Overview", true)}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div style={{ background: "var(--color-background-secondary, #F4F4F5)", borderRadius: 10, padding: 12, border: "0.5px solid var(--color-border-tertiary, #E5E7EB)" }}>
                <p style={{ fontSize: 22, fontWeight: 500, color: "var(--color-text-primary, #111)", margin: 0 }}>{utilisationPct}%</p>
                <p style={{ fontSize: 12, color: "var(--color-text-secondary, #6B7280)", margin: "2px 0 0" }}>wardrobe utilisation</p>
                <p style={{ fontSize: 12, color: "var(--color-text-tertiary, #9CA3AF)", margin: "4px 0 0" }}>
                  {wornItemIds.size} of {wardrobeItems.length} items worn
                </p>
              </div>
              <div style={{ background: "var(--color-background-secondary, #F4F4F5)", borderRadius: 10, padding: 12, border: "0.5px solid var(--color-border-tertiary, #E5E7EB)" }}>
                <p style={{ fontSize: 22, fontWeight: 500, color: "var(--color-text-primary, #111)", margin: 0 }}>{wornOnly.length}</p>
                <p style={{ fontSize: 12, color: "var(--color-text-secondary, #6B7280)", margin: "2px 0 0" }}>looks logged</p>
                <p style={{ fontSize: 12, color: "var(--color-text-tertiary, #9CA3AF)", margin: "4px 0 0" }}>{thisWeekLooks} this week</p>
              </div>
            </div>

            {wardrobeItems.length >= 5 && missingCategories.length > 0 && (
              <>
                {sectionHeader("Wardrobe gaps", false)}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {missingCategories.map((c) => (
                    <div key={c.key} style={{ background: "#FAECE7", borderRadius: 10, padding: "12px 14px", border: "0.5px solid #F5C4B3", display: "flex", flexDirection: "column", gap: 6 }}>
                      <p style={{ fontSize: 13, fontWeight: 500, color: "#712B13", margin: 0 }}>You have no {c.label}</p>
                      <p style={{ fontSize: 12, color: "#993C1D", margin: 0, lineHeight: 1.5 }}>{c.message}</p>
                      <button
                        onClick={() => { if (onGoToShopping) onGoToShopping(); }}
                        style={{ background: "transparent", border: "none", fontSize: 12, color: "#FF5C5C", cursor: "pointer", padding: 0, textAlign: "left", fontWeight: 500 }}
                      >
                        Find {c.label} to buy →
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {wornOnly.length >= 3 && topItems.length > 0 && (
              <>
                {sectionHeader("Most worn items", false)}
                <div>
                  {topItems.map((row, index) => (
                    <div
                      key={row.item.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 0",
                        borderBottom: index === topItems.length - 1 ? "none" : "0.5px solid var(--color-border-tertiary, #E5E7EB)",
                      }}
                    >
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        background: index === 0 ? "#FF5C5C" : "var(--color-background-tertiary, #E5E7EB)",
                        border: index === 0 ? "none" : "0.5px solid var(--color-border-secondary, #E5E7EB)",
                        flexShrink: 0,
                      }} />
                      <p style={{ fontSize: 13, color: "var(--color-text-primary, #111)", margin: 0, flex: 1 }}>{row.item.name}</p>
                      <p style={{ fontSize: 12, color: "var(--color-text-secondary, #6B7280)", margin: 0, flexShrink: 0 }}>{row.count}×</p>
                    </div>
                  ))}
                </div>
              </>
            )}

            {wornOnly.length >= 2 && streak >= 2 && (
              <>
                {sectionHeader("Streak", false)}
                <div style={{ background: "#EAF3DE", borderRadius: 10, padding: "12px 14px", border: "0.5px solid #97C459" }}>
                  <p style={{ fontSize: 12, fontWeight: 500, color: "#3B6D11", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Style streak</p>
                  <p style={{ fontSize: 22, fontWeight: 500, color: "#27500A", margin: "0 0 3px" }}>{streak} days</p>
                  <p style={{ fontSize: 12, color: "#3B6D11", margin: 0 }}>
                    {streak === 2
                      ? "Two days in a row — keep it going!"
                      : streak >= 7
                        ? `${streak} days straight — you're on fire!`
                        : `${streak} days in a row — great habit!`}
                  </p>
                </div>
              </>
            )}

            {wornOnly.length >= 5 && topOccasion && (
              <>
                {sectionHeader("Your style", false)}
                <div style={{ background: "var(--color-background-secondary, #F4F4F5)", borderRadius: 10, padding: "12px 14px", border: "0.5px solid var(--color-border-tertiary, #E5E7EB)" }}>
                  <p style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary, #6B7280)", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Your style</p>
                  <p style={{ fontSize: 13, color: "var(--color-text-primary, #111)", margin: "0 0 3px", lineHeight: 1.4 }}>
                    Most of your logged looks are{" "}
                    <span style={{ fontWeight: 500, color: "#FF5C5C" }}>{topOccasion[0]}</span>
                  </p>
                  <p style={{ fontSize: 12, color: "var(--color-text-secondary, #6B7280)", margin: 0 }}>
                    {topOccasion[0] === "work"
                      ? "You dress well for work — try a weekend look"
                      : topOccasion[0] === "casual"
                        ? "Try a smart casual look to mix things up"
                        : `${topOccasion[0]} looks are your signature style`}
                  </p>
                </div>
              </>
            )}
          </div>
        );
      })()}




      {detecting && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(44,40,32,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#FFFCF8", borderRadius: 20, padding: "20px 24px", boxShadow: "0 12px 32px rgba(44,40,32,0.18)", maxWidth: 240, textAlign: "center" }}>
            <i className="ti ti-loader animate-spin" style={{ fontSize: 24, color: "#C84B2F", display: "inline-block" }} aria-hidden="true" />
            <div style={{ fontSize: 13, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", marginTop: 10 }}>Detecting items...</div>
          </div>
        </div>
      )}

      {addSheetOpen && (() => {
        const rows: { icon: string; title: string; body: string; onClick: () => void }[] = [
          {
            icon: "ti-camera",
            title: "My clothing photos",
            body: "Photos you've already taken of individual items",
            onClick: () => { setAddSheetOpen(false); singleFileRef.current?.click(); },
          },
          {
            icon: "ti-photo",
            title: "Photo of a full outfit",
            body: "Selfie or worn outfit — we'll separate each item",
            onClick: () => { setAddSheetOpen(false); setTimeout(() => outfitFileRef.current?.click(), 50); },
          },
          {
            icon: "ti-archive",
            title: "Import from past analyses",
            body: "Add items you've already detected",
            onClick: () => { setAddSheetOpen(false); setImportOpen(true); },
          },
        ];
        return (
          <>
            <div onClick={() => setAddSheetOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(44,40,32,0.4)", zIndex: 90 }} />
            <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", left: 0, right: 0, bottom: "calc(78px + var(--safe-bottom, 0px))", background: "#FFFCF8", borderRadius: "22px 22px 0 0", maxHeight: "80vh", overflowY: "auto", zIndex: 100, boxShadow: "0 -10px 24px rgba(0,0,0,0.18)" }}>
              <div style={{ padding: "8px 0", display: "flex", justifyContent: "center" }}>
                <div style={{ width: 36, height: 4, background: "#E0D8D0", borderRadius: 2 }} />
              </div>
              <div style={{ padding: "0 16px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div className="l2f-type-section-header" style={{ color: "#9B958E" }}>ADD TO WARDROBE</div>
                  <div style={{ fontSize: 18, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", lineHeight: 1.1, marginTop: 1 }}>Choose a method</div>
                </div>
                <button onClick={() => setAddSheetOpen(false)} aria-label="Close" style={{ width: 32, height: 32, borderRadius: "50%", background: "#F1EFE8", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                  <i className="ti ti-x" style={{ fontSize: 14, color: "#4A4540" }} aria-hidden="true" />
                </button>
              </div>
              <div style={{ margin: "0 16px 16px" }}>
                {rows.map((r, idx) => (
                  <button key={r.icon} onClick={r.onClick} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px 14px", background: "transparent", border: "none", textAlign: "left", borderBottom: idx === rows.length - 1 ? "none" : "0.5px solid #F1EFE8", cursor: "pointer" }}>
                    <div style={{ width: 40, height: 40, borderRadius: 14, background: "#FAECE7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className={`ti ${r.icon}`} style={{ fontSize: 18, color: "#C84B2F" }} aria-hidden="true" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, color: "#2C2820", fontWeight: 500, margin: 0 }}>{r.title}</p>
                      <p style={{ fontSize: 12, color: "#6B6560", margin: "1px 0 0", lineHeight: 1.4 }}>{r.body}</p>
                    </div>
                    <i className="ti ti-chevron-right" style={{ fontSize: 14, color: "#9B958E" }} aria-hidden="true" />
                  </button>
                ))}
              </div>
            </div>
          </>
        );
      })()}

      {singleConfirm && (
        <>
          <div onClick={() => setSingleConfirm(null)} style={{ position: "fixed", inset: 0, background: "rgba(44,40,32,0.4)", zIndex: 90 }} />
          <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", left: 0, right: 0, bottom: "calc(78px + var(--safe-bottom, 0px))", background: "#FFFCF8", borderRadius: "22px 22px 0 0", maxHeight: "80vh", overflowY: "auto", zIndex: 100, boxShadow: "0 -10px 24px rgba(0,0,0,0.18)" }}>
            <div style={{ padding: "8px 0", display: "flex", justifyContent: "center" }}>
              <div style={{ width: 36, height: 4, background: "#E0D8D0", borderRadius: 2 }} />
            </div>
            <div style={{ padding: "0 16px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div className="l2f-type-section-header" style={{ color: "#9B958E" }}>CONFIRM ITEM</div>
                <div style={{ fontSize: 18, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", lineHeight: 1.1, marginTop: 1 }}>Looks good?</div>
              </div>
              <button onClick={() => setSingleConfirm(null)} aria-label="Close" style={{ width: 32, height: 32, borderRadius: "50%", background: "#F1EFE8", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                <i className="ti ti-x" style={{ fontSize: 14, color: "#4A4540" }} aria-hidden="true" />
              </button>
            </div>

            {/* Preview tile */}
            <div style={{ margin: "0 16px 16px", padding: "12px 14px", background: "#F1EFE8", borderRadius: 14, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 56, height: 56, borderRadius: 10, background: singleConfirm.color_hex, overflow: "hidden", flexShrink: 0 }}>
                <img loading="lazy" decoding="async" src={singleConfirm.dataUrl} className="w-full h-full object-cover" alt="" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", textTransform: "capitalize" }}>
                  Detected as {singleConfirm.category}
                </div>
                <div style={{ fontSize: 12, color: "#9B958E", marginTop: 2 }}>Edit anything that's off below</div>
              </div>
            </div>

            {/* Name */}
            <div className="l2f-type-section-header" style={{ color: "#9B958E", padding: "0 16px 6px" }}>NAME</div>
            <div style={{ padding: "0 16px 14px" }}>
              <input
                type="text"
                value={singleConfirm.name}
                onChange={(e) => setSingleConfirm({ ...singleConfirm, name: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", background: "#FFFCF8", border: "1px solid #E0D8D0", borderRadius: 12, fontSize: 16, color: "#2C2820", boxSizing: "border-box", outline: "none" }}
              />
            </div>

            {/* Category */}
            <div className="l2f-type-section-header" style={{ color: "#9B958E", padding: "0 16px 6px" }}>CATEGORY</div>
            <div style={{ padding: "0 16px 16px" }}>
              <div className="hide-scrollbar" style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }}>
                {CATEGORIES.map((c) => {
                  const active = singleConfirm.category === c;
                  return (
                    <button
                      key={c}
                      onClick={() => { triggerHaptic("selection"); setSingleConfirm({ ...singleConfirm, category: c }); }}
                      style={{ display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, borderRadius: 14, padding: "5px 11px", fontSize: 12, background: active ? "#FAECE7" : "#FFFCF8", color: active ? "#712B13" : "#4A4540", border: active ? "1.5px solid #C84B2F" : "1px solid #E0D8D0", fontWeight: active ? 500 : 400, cursor: "pointer", textTransform: "capitalize" }}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: "0 16px 16px", display: "flex", gap: 8 }}>
              <button onClick={() => setSingleConfirm(null)} style={{ flex: 1, background: "transparent", border: "1px solid rgba(0,0,0,0.15)", color: "#4A4540", borderRadius: 22, padding: 11, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={() => { triggerHaptic("success"); saveSingleItem(); }} style={{ flex: 1.4, background: "#C84B2F", color: "#fff", border: "none", borderRadius: 22, padding: 11, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                Save to wardrobe
              </button>
            </div>
          </div>
        </>
      )}

      {outfitConfirm && (() => {
        const selectedCount = outfitConfirm.items.filter((i) => i.selected).length;
        const totalCount = outfitConfirm.items.length;
        const ctaLabel = selectedCount === 0 ? "Add items" : selectedCount === 1 ? "Add 1 item" : `Add ${selectedCount} items`;
        const ctaDisabled = selectedCount === 0;
        return (
          <>
            <div onClick={() => setOutfitConfirm(null)} style={{ position: "fixed", inset: 0, background: "rgba(44,40,32,0.4)", zIndex: 90 }} />
            <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", left: 0, right: 0, bottom: "calc(78px + var(--safe-bottom, 0px))", background: "#FFFCF8", borderRadius: "22px 22px 0 0", maxHeight: "80vh", display: "flex", flexDirection: "column", zIndex: 100, boxShadow: "0 -10px 24px rgba(0,0,0,0.18)" }}>
              <div style={{ padding: "8px 0", display: "flex", justifyContent: "center" }}>
                <div style={{ width: 36, height: 4, background: "#E0D8D0", borderRadius: 2 }} />
              </div>
              <div style={{ padding: "0 16px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div className="l2f-type-section-header" style={{ color: "#9B958E" }}>SELECT ITEMS</div>
                  <div style={{ fontSize: 18, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", lineHeight: 1.1, marginTop: 1 }}>{selectedCount} of {totalCount} selected</div>
                </div>
                <button onClick={() => setOutfitConfirm(null)} aria-label="Close" style={{ width: 32, height: 32, borderRadius: "50%", background: "#F1EFE8", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                  <i className="ti ti-x" style={{ fontSize: 14, color: "#4A4540" }} aria-hidden="true" />
                </button>
              </div>

              <div style={{ maxHeight: 280, overflowY: "auto", flex: "0 1 auto" }}>
                {outfitConfirm.items.map((item, idx) => {
                  const isLast = idx === outfitConfirm.items.length - 1;
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        triggerHaptic("selection");
                        setOutfitConfirm({
                          ...outfitConfirm,
                          items: outfitConfirm.items.map((i, j) => (j === idx ? { ...i, selected: !i.selected } : i)),
                        });
                      }}
                      style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "10px 14px", background: "transparent", border: "none", textAlign: "left", borderBottom: isLast ? "none" : "0.5px solid #F1EFE8", cursor: "pointer" }}
                    >
                      <div style={{ width: 40, height: 40, borderRadius: 8, background: item.color_hex, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, color: "#2C2820", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</p>
                        <p style={{ fontSize: 12, color: "#9B958E", margin: "1px 0 0", textTransform: "capitalize" }}>{item.category}</p>
                      </div>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", border: item.selected ? "1.5px solid #C84B2F" : "1.5px solid #E0D8D0", background: item.selected ? "#C84B2F" : "#FFFCF8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {item.selected && <i className="ti ti-check" style={{ fontSize: 12, color: "#fff" }} aria-hidden="true" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div style={{ padding: "14px 16px", display: "flex", gap: 8, borderTop: "0.5px solid #F1EFE8" }}>
                <button onClick={() => setOutfitConfirm(null)} style={{ flex: 1, background: "transparent", border: "1px solid rgba(0,0,0,0.15)", color: "#4A4540", borderRadius: 22, padding: 11, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                  Cancel
                </button>
                <button
                  onClick={() => { if (ctaDisabled) return; triggerHaptic("success"); saveOutfitItems(); }}
                  disabled={ctaDisabled}
                  style={{ flex: 1.4, background: ctaDisabled ? "#D3D1C7" : "#C84B2F", color: ctaDisabled ? "#9B958E" : "#fff", border: "none", borderRadius: 22, padding: 11, fontSize: 13, fontWeight: 500, cursor: ctaDisabled ? "not-allowed" : "pointer" }}
                >
                  {ctaLabel}
                </button>
              </div>
            </div>
          </>
        );
      })()}

      {importOpen && (
        <ImportFromPast
          userId={userId}
          existingItems={wardrobeItems}
          onClose={() => setImportOpen(false)}
          onAdded={(added) => {
            setWardrobeItems((prev) => [...added, ...prev]);
            trackEvent("wardrobe_items_added", {
              source: "detected_item",
              count_added: added.length,
              count_failed: 0,
              total_wardrobe_size_after: wardrobeItems.length + added.length,
            });
          }}
        />
      )}

      <EditItemSheet
        item={actionItem}
        onClose={() => setActionItem(null)}
        onSave={handleSaveItem}
        onDelete={handleConfirmDelete}
      />


      {/* Underused suggestion sheet */}
      {underusedSuggest && (
        <>
          <div onClick={() => setUnderusedSuggest(null)} style={{ position: "fixed", inset: 0, background: "rgba(44,40,32,0.4)", zIndex: 90 }} />
          <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", left: 0, right: 0, bottom: "calc(78px + var(--safe-bottom, 0px))", background: "#FFFCF8", borderRadius: "22px 22px 0 0", maxHeight: "80vh", overflowY: "auto", zIndex: 100, boxShadow: "0 -10px 24px rgba(0,0,0,0.18)" }}>
            <div style={{ padding: "8px 0", display: "flex", justifyContent: "center" }}>
              <div style={{ width: 36, height: 4, background: "#E0D8D0", borderRadius: 2 }} />
            </div>
            <div style={{ padding: "0 16px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="l2f-type-section-header" style={{ color: "#9B958E" }}>STYLE THIS</div>
                <div style={{ fontSize: 18, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", lineHeight: 1.1, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{underusedSuggest.name}</div>
              </div>
              <button onClick={() => setUnderusedSuggest(null)} aria-label="Close" style={{ width: 32, height: 32, borderRadius: "50%", background: "#F1EFE8", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                <i className="ti ti-x" style={{ fontSize: 14, color: "#4A4540" }} aria-hidden="true" />
              </button>
            </div>
            <div style={{ padding: "0 16px 14px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 10, background: underusedSuggest.cutout_status === "ready" ? "var(--surface-2)" : (underusedSuggest.color_hex ?? "#F4F4F5"), overflow: "hidden", flexShrink: 0 }}>
                <WardrobeImg item={underusedSuggest} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)" }}>Use this as today's starting point</div>
                <div style={{ fontSize: 12, color: "#6B6560", marginTop: 2 }}>{lastWornLabel(underusedSuggest)}</div>
              </div>
            </div>
            <div style={{ padding: "0 16px 16px", display: "flex", gap: 8 }}>
              <button
                onClick={() => setUnderusedSuggest(null)}
                style={{ flex: 1, background: "transparent", border: "1px solid rgba(0,0,0,0.15)", color: "#4A4540", borderRadius: 22, padding: 11, fontSize: 13, fontWeight: 500, cursor: "pointer" }}
              >
                Not now
              </button>
              <button
                onClick={() => { triggerHaptic("success"); triggerGenerateFromUnderused(underusedSuggest); }}
                style={{ flex: 1.4, background: "#C84B2F", color: "#fff", border: "none", borderRadius: 22, padding: 11, fontSize: 13, fontWeight: 500, cursor: "pointer" }}
              >
                Generate look
              </button>
            </div>
          </div>
        </>
      )}

      {detailOutfit && (
        <>
          <div onClick={() => setDetailOutfit(null)} style={{ position: "fixed", inset: 0, background: "rgba(44,40,32,0.4)", zIndex: 90 }} />
          <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", left: 0, right: 0, bottom: "calc(78px + var(--safe-bottom, 0px))", background: "#FFFCF8", borderRadius: "22px 22px 0 0", maxHeight: "80vh", display: "flex", flexDirection: "column", zIndex: 100, boxShadow: "0 -10px 24px rgba(0,0,0,0.18)" }}>
            <div style={{ padding: "8px 0", display: "flex", justifyContent: "center", flexShrink: 0 }}>
              <div style={{ width: 36, height: 4, background: "#E0D8D0", borderRadius: 2 }} />
            </div>
            <div style={{ padding: "0 16px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, textTransform: "uppercase", color: "#9B958E", letterSpacing: "0.08em", fontWeight: 500 }}>{detailOutfit.worn_at ? "WORN" : "SAVED"}</div>
                <div style={{ fontSize: 18, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", lineHeight: 1.1, marginTop: 1 }}>{detailOutfit.worn_at ? formatLongDate(detailOutfit.worn_at) : "Saved for later"}</div>
              </div>
              <button onClick={() => setDetailOutfit(null)} aria-label="Close" style={{ width: 32, height: 32, borderRadius: "50%", background: "#F1EFE8", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                <i className="ti ti-x" style={{ fontSize: 14, color: "#4A4540" }} aria-hidden="true" />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              <div style={{ padding: "0 16px 12px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                {detailOutfit.occasion && (
                  <span style={{ background: "#FAECE7", border: "1px solid #C84B2F", borderRadius: 14, padding: "4px 10px", fontSize: 12, color: "#712B13", fontWeight: 500, textTransform: "capitalize" }}>{detailOutfit.occasion.replace(/_/g, " ")}</span>
                )}
                {detailOutfit.weather && (
                  <span style={{ background: "#FFFCF8", border: "1px solid #E0D8D0", borderRadius: 14, padding: "4px 10px", fontSize: 12, color: "#4A4540", textTransform: "capitalize" }}>{detailOutfit.weather}</span>
                )}
                {detailOutfit.mood && (
                  <span style={{ background: "#FFFCF8", border: "1px solid #E0D8D0", borderRadius: 14, padding: "4px 10px", fontSize: 12, color: "#4A4540", textTransform: "capitalize" }}>{detailOutfit.mood}</span>
                )}
              </div>
              <div style={{ padding: "0 16px 12px" }}>
                <div style={{ background: "#FFFCF8", border: "0.5px solid #E8E2D9", borderRadius: 16, overflow: "hidden" }}>
                  {detailOutfit.generated_image_url ? (
                    <img loading="lazy" decoding="async" src={detailOutfit.generated_image_url} alt="" style={{ width: "100%", height: 200, objectFit: "cover", display: "block" }} />
                  ) : (
                    <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <i className="ti ti-photo" style={{ fontSize: 24, color: "#9B958E" }} aria-hidden="true" />
                    </div>
                  )}
                </div>
              </div>
              <div style={{ padding: "0 16px 12px" }}>
                <div style={{ fontSize: 12, textTransform: "uppercase", color: "#9B958E", letterSpacing: "0.08em", fontWeight: 500, marginBottom: 8 }}>ITEMS YOU WORE</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {detailOutfit.wardrobe_item_ids.map((id) => {
                    const it = wornById.get(id);
                    if (!it) {
                      return (
                        <div key={id} style={{ display: "flex", alignItems: "center", gap: 10, opacity: 0.5 }}>
                          <div style={{ width: 24, height: 24, borderRadius: 6, background: "#D1D5DB" }} />
                          <p style={{ fontSize: 12, margin: 0, textDecoration: "line-through", color: "#6B6560", fontStyle: "italic" }}>Item removed from wardrobe</p>
                        </div>
                      );
                    }
                    return (
                      <div key={id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 24, height: 24, borderRadius: 6, background: it.color_hex ?? "#D1D5DB", overflow: "hidden", flexShrink: 0 }}>
                          <WardrobeImg item={it} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                        <p style={{ fontSize: 12, color: "#2C2820", margin: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</p>
                        <span style={{ fontSize: 12, color: "#9B958E", textTransform: "lowercase" }}>{it.category}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ padding: "0 16px 16px", flexShrink: 0 }}>
              <button
                onClick={() => { triggerHaptic("success"); wearAgain(detailOutfit); }}
                style={{ width: "100%", background: "#C84B2F", color: "#fff", border: "none", borderRadius: 22, padding: 11, fontSize: 13, fontWeight: 500, cursor: "pointer" }}
              >
                Wear this again ↗
              </button>
            </div>
          </div>
        </>
      )}


      {/* Full history screen */}
      {historyView === "all" && (
        <div className="fixed inset-0 z-40 bg-background overflow-y-auto">
          <div className="px-4 pt-10 pb-24">
            <button onClick={() => setHistoryView("main")} className="text-sm font-medium mb-4 flex items-center gap-1">
              ← Outfit History
            </button>
            {(() => {
              const groups = new Map<string, WornOutfit[]>();
              wornOutfits.forEach((o) => {
                const k = monthKey(o.worn_at ?? o.created_at ?? new Date().toISOString());
                if (!groups.has(k)) groups.set(k, []);
                groups.get(k)!.push(o);
              });
              return Array.from(groups.entries()).map(([month, outfits]) => {
                const counts: Record<string, number> = {};
                outfits.forEach((o) => { const k = o.occasion ?? "casual"; counts[k] = (counts[k] ?? 0) + 1; });
                const topOcc = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
                const itemCounts: Record<string, number> = {};
                outfits.forEach((o) => o.wardrobe_item_ids.forEach((id) => { itemCounts[id] = (itemCounts[id] ?? 0) + 1; }));
                const favId = Object.entries(itemCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
                const favName = favId ? wornById.get(favId)?.name : null;
                return (
                  <div key={month} style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: 16, fontWeight: 500, margin: "0 0 2px" }}>{month}</p>
                    <p style={{ fontSize: 12, color: "#9CA3AF", margin: "0 0 10px" }}>
                      {outfits.length} outfit{outfits.length === 1 ? "" : "s"}
                      {topOcc && ` · Most worn: ${topOcc.replace(/_/g, " ")}`}
                      {favName && ` · Favourite item: ${favName}`}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {outfits.map((o) => (
                        <button key={o.id} onClick={() => setDetailOutfit(o)} className="text-left">
                          <div style={{ aspectRatio: "3/4", borderRadius: 12, overflow: "hidden", background: "#F4F4F5", border: "0.5px solid #E5E7EB" }}>
                            {o.generated_image_url ? (
                              <img  loading="lazy" decoding="async"src={o.generated_image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", width: "100%", height: "100%" }}>
                                {o.wardrobe_item_ids.slice(0, 4).map((id, i) => (
                                  <div key={i} style={{ background: wornById.get(id)?.color_hex ?? "#E5E7EB" }} />
                                ))}
                              </div>
                            )}
                          </div>
                          <p style={{ fontSize: 12, fontWeight: 500, margin: "6px 0 0", textTransform: "capitalize" }}>
                            {(o.occasion ?? "casual").replace(/_/g, " ")} · {o.worn_at ? formatShortDate(o.worn_at) : "Saved"}
                          </p>
                          <p style={{ fontSize: 12, color: "#9CA3AF", margin: 0 }}>Tap to see details</p>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}
      {carouselOpen && selectedLookItems.length > 0 && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 100, display: "flex", flexDirection: "column", padding: 0 }}
          onClick={(e) => { if (e.target === e.currentTarget) closeCarousel(); }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "52px 20px 16px" }}>
            <div>
              <p style={{ fontSize: 16, fontWeight: 500, color: "white", margin: "0 0 3px" }}>
                {selectedLookItems[carouselIndex]?.name}
              </p>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", margin: 0, textTransform: "capitalize" }}>
                {selectedLookItems[carouselIndex]?.category} · {carouselIndex + 1} of {selectedLookItems.length}
              </p>
            </div>
            <button
              onClick={() => closeCarousel()}
              style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
              aria-label="Close"
            >
              <i className="ti ti-x" style={{ fontSize: 16, color: "white" }} aria-hidden="true" />
            </button>
          </div>
          <div
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 20px", position: "relative" }}
            onTouchStart={(e) => { setTouchStart(e.touches[0].clientX); }}
            onTouchEnd={(e) => {
              if (touchStart === null) return;
              const diff = touchStart - e.changedTouches[0].clientX;
              if (Math.abs(diff) > 50) {
                if (diff > 0 && carouselIndex < selectedLookItems.length - 1) {
                  setCarouselIndex((prev) => prev + 1);
                } else if (diff < 0 && carouselIndex > 0) {
                  setCarouselIndex((prev) => prev - 1);
                }
              }
              setTouchStart(null);
            }}
          >
            {carouselIndex > 0 && (
              <button
                onClick={() => setCarouselIndex((prev) => prev - 1)}
                style={{ position: "absolute", left: 8, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 1 }}
                aria-label="Previous item"
              >
                <i className="ti ti-chevron-left" style={{ fontSize: 18, color: "white" }} aria-hidden="true" />
              </button>
            )}
            <div style={{
              width: "100%",
              maxWidth: 320,
              aspectRatio: "3/4",
              borderRadius: 16,
              background: selectedLookItems[carouselIndex]?.color_hex ?? "#333",
              border: "0.5px solid rgba(255,255,255,0.1)",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              {selectedLookItems[carouselIndex] ? (
                <WardrobeImg
                  item={selectedLookItems[carouselIndex]}
                  transformWidth={800}
                  transformQuality={85}
                  alt={selectedLookItems[carouselIndex]?.name ?? ""}
                  style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                />
              ) : null}
            </div>
            {carouselIndex < selectedLookItems.length - 1 && (
              <button
                onClick={() => setCarouselIndex((prev) => prev + 1)}
                style={{ position: "absolute", right: 8, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 1 }}
                aria-label="Next item"
              >
                <i className="ti ti-chevron-right" style={{ fontSize: 18, color: "white" }} aria-hidden="true" />
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", padding: "16px 20px 40px", overflowX: "auto" }}>
            {selectedLookItems.map((item, index) => (
              <div
                key={item.id}
                onClick={() => setCarouselIndex(index)}
                style={{
                  width: 44,
                  height: 52,
                  borderRadius: 8,
                  background: item.color_hex ?? "#333",
                  border: index === carouselIndex ? "2px solid white" : "2px solid rgba(255,255,255,0.2)",
                  opacity: index === carouselIndex ? 1 : 0.5,
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "opacity 0.2s, border 0.2s",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <WardrobeImg
                  item={item}
                  transformWidth={120}
                  transformQuality={80}
                  alt={item.name ?? ""}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <WardrobeDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        itemCount={wardrobeItems.length}
        historyCount={wornOutfits.length}
        currentRoute={drawerRoute}
        onNavigateRail={() => { setDrawerOpen(false); setDrawerRoute("style-yourself"); }}
        onNavigateSavedLooks={() => { setDrawerOpen(false); setDrawerRoute("saved-looks"); setSavedView(null); }}
        onNavigateItems={() => { setDrawerOpen(false); setDrawerRoute("items"); }}
        onNavigateHistory={() => { setDrawerOpen(false); setDrawerRoute("history"); }}
        onNavigateInsights={() => { setDrawerOpen(false); setDrawerRoute("insights"); }}
        onAddItem={() => { setDrawerOpen(false); setAddSheetOpen(true); }}
        onOpenHelp={() => { setDrawerOpen(false); navigate({ to: "/help" }); }}
      />

    </div>

  );
}

type PastItem = {
  id: string;
  name: string;
  category: string;
  color_hex: string | null;
  products: any;
  created_at: string;
  outfit_id: string;
  material: string | null;
  pattern: string | null;
  style_tags: string[] | null;
  vision_description: string | null;
  outfits?: { lb2_style_analysis: any } | null;
};

function ImportFromPast({
  userId,
  existingItems,
  onClose,
  onAdded,
}: {
  userId: string;
  existingItems: WardrobeItem[];
  onClose: () => void;
  onAdded: (added: WardrobeItem[]) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [pastItems, setPastItems] = useState<PastItem[]>([]);
  const [adding, setAdding] = useState<Set<string>>(new Set());
  const [addedIds, setAddedIds] = useState<Set<string>>(
    new Set(existingItems.map((i) => i.detected_item_id).filter((x): x is string => !!x)),
  );
  const [bulkAdding, setBulkAdding] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("detected_items")
        .select("id, name, category, color_hex, products, created_at, outfit_id, material, pattern, style_tags, vision_description, outfits!inner(user_id, lb2_style_analysis)")
        .eq("outfits.user_id", userId)
        .not("products", "is", null)
        .order("created_at", { ascending: false });
      if (error) {
        console.error(error);
        toast.error("Couldn't load past items.");
        setLoading(false);
        return;
      }
      const rows = (data ?? []) as any[];
      const seen = new Set<string>();
      const unique: PastItem[] = [];
      for (const r of rows) {
        const name = (r.name ?? "").trim();
        if (!name) continue;
        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(r as PastItem);
      }
      setPastItems(unique);
      setLoading(false);
    })();
  }, [userId]);


  const addOne = async (item: PastItem) => {
    if (addedIds.has(item.id) || adding.has(item.id)) return;
    setAdding((prev) => new Set(prev).add(item.id));
    const thumbnail =
      Array.isArray(item.products) && item.products[0]?.thumbnail
        ? (item.products[0].thumbnail as string)
        : null;
    const { data, error } = await supabase
      .from("wardrobe_items")
      .insert({
        user_id: userId,
        name: item.name,
        category: item.category,
        color_hex: item.color_hex,
        image_url: thumbnail,
        source: "detected_item",
        detected_item_id: item.id,
        material: item.material ?? null,
        pattern: item.pattern ?? null,
        style_tags: item.style_tags ?? null,
        vision_description: item.vision_description ?? null,
        formality: normaliseFormality(item.outfits?.lb2_style_analysis?.formality),
        occasion_tags: (() => {
          const tag = mapOccasionToTag(item.outfits?.lb2_style_analysis?.occasion);
          return tag ? [tag] : null;
        })(),
        lb3_eligible: deriveLb3Eligible(item),
      })
      .select()
      .single();
    setAdding((prev) => {
      const n = new Set(prev);
      n.delete(item.id);
      return n;
    });
    if (error) {
      toast.error("Couldn't add item.");
      return;
    }
    setAddedIds((prev) => new Set(prev).add(item.id));
    invokeCutout((data as WardrobeItem).id);
    onAdded([data as WardrobeItem]);
  };

  const addAll = async () => {
    const remaining = pastItems.filter((i) => !addedIds.has(i.id));
    if (remaining.length === 0) return;
    setBulkAdding(true);
    const rows = remaining.map((item) => {
      const thumbnail =
        Array.isArray(item.products) && item.products[0]?.thumbnail
          ? (item.products[0].thumbnail as string)
          : null;
      return {
        user_id: userId,
        name: item.name,
        category: item.category,
        color_hex: item.color_hex,
        image_url: thumbnail,
        source: "detected_item",
        detected_item_id: item.id,
        material: item.material ?? null,
        pattern: item.pattern ?? null,
        style_tags: item.style_tags ?? null,
        vision_description: item.vision_description ?? null,
        formality: normaliseFormality(item.outfits?.lb2_style_analysis?.formality),
        occasion_tags: (() => {
          const tag = mapOccasionToTag(item.outfits?.lb2_style_analysis?.occasion);
          return tag ? [tag] : null;
        })(),
        lb3_eligible: deriveLb3Eligible(item),
      };
    });
    const { data, error } = await supabase.from("wardrobe_items").insert(rows).select();
    setBulkAdding(false);
    if (error) {
      toast.error("Couldn't add items.");
      return;
    }
    setAddedIds((prev) => {
      const n = new Set(prev);
      remaining.forEach((i) => n.add(i.id));
      return n;
    });
    onAdded(data as WardrobeItem[]);
    (data as WardrobeItem[]).forEach((row) => invokeCutout(row.id));
    toast.success(`Added ${remaining.length} items`);
  };

  const remainingCount = pastItems.filter((i) => !addedIds.has(i.id)).length;

  const timeAgo = (iso: string) => {
    try {
      const diff = Date.now() - new Date(iso).getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      if (days <= 0) return "today";
      if (days === 1) return "1 day ago";
      if (days < 7) return `${days} days ago`;
      const weeks = Math.floor(days / 7);
      if (weeks === 1) return "1 week ago";
      if (weeks < 5) return `${weeks} weeks ago`;
      const months = Math.floor(days / 30);
      if (months <= 1) return "1 month ago";
      return `${months} months ago`;
    } catch {
      return "";
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 40, background: "#E8E0D5", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "calc(var(--safe-top, 0px) + 8px) 14px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onClose} aria-label="Back to wardrobe" style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.5)", border: "1px solid rgba(0,0,0,0.07)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 14, color: "#4A4540" }} aria-hidden="true" />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="l2f-type-section-header" style={{ color: "#C84B2F" }}>LIKE2FIT</div>
          <div style={{ fontSize: 19, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", lineHeight: 1.1, marginTop: 1 }}>Import from past</div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.5)", border: "1px solid rgba(0,0,0,0.07)", borderRadius: 12, padding: "3px 8px", fontSize: 12, color: "#4A4540", flexShrink: 0 }}>
          {remainingCount} found
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "0 14px" }}>
        {loading ? (
          <div style={{ padding: "40px 16px", textAlign: "center" }}>
            <i className="ti ti-loader animate-spin" style={{ fontSize: 24, color: "#C84B2F", display: "inline-block" }} aria-hidden="true" />
            <div style={{ fontSize: 12, color: "#6B6560", marginTop: 10 }}>Looking through your past analyses...</div>
          </div>
        ) : pastItems.length === 0 ? (
          <div style={{ background: "#FFFCF8", borderRadius: 16, padding: "24px 16px", textAlign: "center", margin: "12px 0" }}>
            <i className="ti ti-archive" style={{ fontSize: 32, color: "#C84B2F" }} aria-hidden="true" />
            <div style={{ fontSize: 18, fontStyle: "italic", color: "#2C2820", fontFamily: "var(--font-serif)", marginTop: 8 }}>Nothing to import yet</div>
            <div style={{ fontSize: 12, color: "#6B6560", lineHeight: 1.5, marginTop: 4 }}>Items detected from your past photo analyses will appear here</div>
          </div>
        ) : (
          pastItems.map((item) => {
            const added = addedIds.has(item.id);
            const isAdding = adding.has(item.id);
            return (
              <div key={item.id} style={{ background: "#FFFCF8", border: "0.5px solid #E8E2D9", borderRadius: 14, padding: "10px 14px", marginBottom: 6, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: item.color_hex ?? "#E5E7EB", overflow: "hidden", flexShrink: 0 }}>
                  <WardrobeImg item={item as any} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: "#2C2820", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                  <div style={{ fontSize: 12, color: "#9B958E", marginTop: 1, textTransform: "lowercase" }}>{item.category} · {timeAgo(item.created_at)}</div>
                </div>
                {added ? (
                  <div style={{ background: "#F1EFE8", color: "#6B6560", borderRadius: 14, padding: "5px 11px", fontSize: 12 }}>Added ✓</div>
                ) : isAdding ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, opacity: 0.6 }}>
                    <i className="ti ti-loader animate-spin" style={{ fontSize: 12, display: "inline-block", color: "#6B6560" }} aria-hidden="true" />
                    <span style={{ fontSize: 12, color: "#6B6560" }}>Adding</span>
                  </div>
                ) : (
                  <button
                    onClick={() => addOne(item)}
                    style={{ background: "#C84B2F", color: "#fff", border: "none", borderRadius: 14, padding: "5px 11px", fontSize: 12, fontWeight: 500, cursor: "pointer", flexShrink: 0 }}
                  >
                    + Add
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
      {remainingCount > 0 && (
        <div style={{ padding: "12px 14px calc(var(--safe-bottom, 0px) + 12px)", background: "linear-gradient(to bottom, rgba(232,224,213,0) 0%, #E8E0D5 30%)" }}>
          <button
            onClick={addAll}
            disabled={bulkAdding}
            style={{ width: "100%", padding: 11, background: "#C84B2F", color: "#fff", border: "none", borderRadius: 22, fontSize: 13, fontWeight: 500, cursor: bulkAdding ? "not-allowed" : "pointer", opacity: bulkAdding ? 0.6 : 1 }}
          >
            {bulkAdding ? "Adding…" : `Add all ${remainingCount} to wardrobe`}
          </button>
        </div>
      )}
    </div>
  );
}

export function WardrobeGuestEmpty({ onSignup }: { onSignup: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8 pb-32">
      <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-4" style={{ background: "var(--l2f-cream)" }}>
        <Archive size={28} style={{ color: "var(--l2f-coral)" }} />
      </div>
      <p style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: 22, fontWeight: 400, color: "var(--l2f-ink)" }}>Sign up to build your wardrobe</p>
      <p className="text-sm text-muted-foreground mt-1 mb-5">Save items and generate looks from your own clothes.</p>
      <button onClick={onSignup} className="px-6 py-3 rounded-full font-medium text-white" style={{ background: "#C84B2F" }}>
        Sign up
      </button>
    </div>
  );
}
